import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatSidebar } from '../sidebar/ChatSidebar';
import { ChatWindow } from './ChatWindow';
import { ChatInput } from './ChatInput';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Menu, X, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Sun, Moon, Plus, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useConversation } from '../../hooks/useConversation';
import { useModelSelection } from '../../hooks/useModelSelection';
import { useHighlighting } from '../../hooks/useHighlighting';
import { usePromptBoxInstructions } from '../../hooks/usePromptBoxInstructions';
import { getDisplayedModelID } from '../../services/modelIdService';
import { ModelChip } from './ModelChip';
import { GradeMatrixGenerator } from './GradeMatrixGenerator';
import { ResponseDots } from './ResponseDots';
import { PromptStatusButtons } from './PromptStatusButtons';
import { ContributeModal } from '../ContributeModal';
import { CoopitalVotingModal } from '../CoopitalVotingModal';
import { subscribeToSystemBalance, formatCurrency, BalanceData } from '../../services/accountingService';
import { BalanceSparkline } from '../BalanceSparkline';
import { doc, updateDoc, setDoc, onSnapshot, getDoc, collection, getDocs, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ConversationService } from '../../services/conversationService';
import { CostTiersService, CostTiers } from '../../services/costTiersService';
import { fetchRandomFirstSessionPair } from '../../services/humanDatasetService';
import { SendMessageOptions, PromptSource } from '../../types/messaging';
import { useProdMode } from '../../hooks/useProdMode';
import { useSelfGrade } from '../../hooks/useSelfGrade';
import { useCustomRubrics } from '../../hooks/useCustomRubrics';
import { CustomReviewButton, CustomReviewOverlay } from '../customReview';
import { useQuestionMarks } from '../../hooks/useQuestionMarks';
import { useInteractedControls } from '../../hooks/useInteractedControls';
import { HelpTooltip, HoverHelpTooltip } from '../ui/HelpTooltip';
import { CoachingLine } from './CoachingLine';
import { useCoaching } from '../../hooks/useCoaching';
import { useDefaultSettings } from '../../hooks/useDefaultSettings';
import { useIonoffCost } from '../../hooks/useIonoffCost';
// Legacy password system removed - EUMPWU now enforces payment
import { checkUserBalance, invalidateBalanceCache, UserBalanceResult } from '../../services/userBalanceService';
import { 
  isAuthenticatedWithEmail, 
  signInWithToken,
  getPendingPrompt,
  storePendingPrompt,
  clearPendingPrompt,
  getAnonymousUidForLinking,
  clearAnonymousUidForLinking,
} from '../../services/authService';

const ENABLE_AUTO_DATASET_SUBMISSION = true; // Enabled for Playwright batch testing

export const ChatLayout: React.FC = () => {
  // URL-based routing hooks
  const { conversationId: urlConversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  
  // Helper function to detect mobile/tablet
  const isMobileOrTablet = () => {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isSmallScreen = window.innerWidth < 768;
    return hasTouch && (isMobileUA || isSmallScreen);
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Always start collapsed, expands on first send (desktop only)
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark mode
  
  // Sidebar width state with localStorage persistence
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 256; // Default to 256px (w-64)
  });
  
  // Persist sidebar width to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);
  
  // Fetch prompt box instructions from Firestore
  const { instructions } = usePromptBoxInstructions();
  
  // Fetch PROD_MODE from Firestore (controls visibility of dev-only UI elements)
  const { prodMode } = useProdMode();
  
  // Fetch SELFGRADE setting from Firestore (controls self-grading for AIs > 2)
  const { selfgrade: selfgradeEnabled } = useSelfGrade();
  
  // Question mark help system - fetches help text configs and tracks user interactions
  const { getHelpText, hasHelp } = useQuestionMarks();
  const { markInteracted, shouldShowHelp } = useInteractedControls();
  
  // Coaching system - generates personalized coaching pointers
  const {
    pointer: coachingPointer,
    fullPrompt: coachingFullPrompt,
    isLoading: isCoachingLoading,
    isEnabled: isCoachingEnabled,
    toggleCoaching,
    generateCoaching,
    clearPointer: clearCoachingPointer,
    goToPrevious: goToPreviousCoaching,
    goToNext: goToNextCoaching,
    hasPrevious: coachingHasPrevious,
    hasNext: coachingHasNext,
    apiMetadata: coachingApiMetadata,
  } = useCoaching();
  
  // Custom review rubrics
  const {
    rubrics: customRubrics,
    activeRubricId,
    setActiveRubricId,
    activeRubric,
    createRubric,
  } = useCustomRubrics();
  const [isCustomReviewWizardOpen, setIsCustomReviewWizardOpen] = useState(false);
  
  // Fetch configurable defaults from Firestore (system/config)
  const { defaultPrioritize, defaultAIs, loading: defaultsLoading } = useDefaultSettings();
  
  // Initialize with fallback defaults, then update when Firestore loads
  const [targetCount, setTargetCount] = useState(3);
  const [reasoningEffort, setReasoningEffort] = useState<'default' | 'low' | 'medium' | 'high'>('default');
  const [prioritize, setPrioritize] = useState<'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom'>('intelligence');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [modelListLastUpdated, setModelListLastUpdated] = useState<string | null>(null);
  
  // Track whether defaults have been applied (to avoid overwriting user changes)
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  
  // Admin: Load recent conversations from all users
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [adminConversationCount, setAdminConversationCount] = useState('');
  const [isLoadingAdminConversations, setIsLoadingAdminConversations] = useState(false);
  const [inactiveModels, setInactiveModels] = useState<Set<string>>(new Set());
  // Custom mode: user's manually curated model list (persisted to user profile)
  const [customModels, setCustomModels] = useState<string[]>([]);
  // Track if user has ever customized (added/removed chips) - enables custom slider position
  const [hasCustomized, setHasCustomized] = useState(false);
  
  // Peer review settings toggle - controls visibility of all sliders
  // Default OFF on page load/refresh and conversation switch (no persistence)
  const [isPeerReviewSettingsExpanded, setIsPeerReviewSettingsExpanded] = useState(false);
  
  // Scroll container ref for ResponseDots viewport tracking
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Duplicate message prevention - prevents double-clicks from triggering multiple API calls
  const isSendingMessageRef = useRef(false);
  // Track when URL parameters are applied to allow fetchModels time to run
  const urlParamsAppliedRef = useRef(false);
  // Individual slider expand states removed - now controlled by isPeerReviewSettingsExpanded
  const [isTopSectionCollapsed, setIsTopSectionCollapsed] = useState(true);
  const [cleanViewEnabled, setCleanViewEnabled] = useState(true);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [isBottomSectionCollapsed, setIsBottomSectionCollapsed] = useState(false);
  const [ionoffArrived, setIonoffArrived] = useState(false);
  
  // Mobile triplet controls - collapsed state and visible count
  const [areMobileControlsExpanded, setAreMobileControlsExpanded] = useState(false);
  const [visibleMobileControlsCount, setVisibleMobileControlsCount] = useState(3);
  const [visiblePromptIndex, setVisiblePromptIndex] = useState<number>(-1);
  const isTogglingRef = React.useRef(false);
  const headerSectionRef = React.useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isAisSliderDragging, setIsAisSliderDragging] = useState(false);
  

  // 🎯 PHASE 2: Prompt editing state
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingPromptContent, setEditingPromptContent] = useState<string>('');

  // 🎯 Apply configurable defaults from Firestore when they load
  // Only apply if: (1) not in an existing conversation, (2) user hasn't made changes yet
  useEffect(() => {
    if (!defaultsLoading && !defaultsApplied && !urlConversationId) {
      console.log('🔧 Applying configurable defaults:', { defaultPrioritize, defaultAIs });
      setTargetCount(defaultAIs);
      setPrioritize(defaultPrioritize);
      setDefaultsApplied(true);
    }
  }, [defaultsLoading, defaultsApplied, defaultPrioritize, defaultAIs, urlConversationId]);

  // 🎯 Force cleanViewEnabled when prodMode is on
  // This ensures debug messages (Shannon, prompt analysis) are always hidden in prodMode
  useEffect(() => {
    if (prodMode && !cleanViewEnabled) {
      setCleanViewEnabled(true);
    }
  }, [prodMode, cleanViewEnabled]);

  // Top windowshade control - toggles only the header/controls section
  const toggleWindowshades = () => {
    setIsTopSectionCollapsed(prev => !prev);
  };

  // Bottom windowshade control - toggles only the prompt box section
  const toggleBottomSection = () => {
    setIsBottomSectionCollapsed(prev => !prev);
  };

  const [costTiers, setCostTiers] = useState<CostTiers | null>(null);
  const [modelPricing, setModelPricing] = useState<Map<string, {prompt: number; completion: number}>>(new Map());
  
  // Toggle controls
  const [webEnabled, setWebEnabled] = useState(true); // default ON
  const [enterSendsEnabled, setEnterSendsEnabled] = useState(!isMobileOrTablet()); // default ON for desktop, OFF for mobile
  const [selfEvalEnabled, setSelfEvalEnabled] = useState(false); // default OFF
  const [txtEnabled, setTxtEnabled] = useState(false); // default OFF - full round-robin tournament grading
  const [toolsExpanded, setToolsExpanded] = useState(false); // default OFF - other controls hidden until user clicks "tools"
  const [plusHumanEnabled, setPlusHumanEnabled] = useState(false); // default OFF - +human feature
  const [humanLeaderboardEnabled, setHumanLeaderboardEnabled] = useState(true); // default ON - contribute to global leaderboard
  const [autoHumanDatasetEnabled, setAutoHumanDatasetEnabled] = useState(false);
  const [dismissedIcebreakers, setDismissedIcebreakers] = useState<string[]>([]); // Exact icebreaker texts user has dismissed
  
  // Track which models have their $ margin toggled
  const [marginToggledModels, setMarginToggledModels] = useState<Set<string>>(new Set());
  
  // Track whether all chips show model names (true) or creator names (false)
  const [showModelNames, setShowModelNames] = useState<boolean>(false);
  
  // Model search state
  const [isModelSearchOpen, setIsModelSearchOpen] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const [allOpenRouterModels, setAllOpenRouterModels] = useState<Array<{id: string; name: string; combinedRank: number | null; defaultReasoningEffort: string | null}>>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  
  // Track prompt source (URL vs manual) for debug info
  const [promptSource, setPromptSource] = useState<PromptSource>('manual');
  const [baselineFromUrl, setBaselineFromUrl] = useState(false);
  
  const datasetAutoSubmitRef = useRef(false);
  
  const { user, loading: authLoading, signInAnonymously } = useAuth();
  const { 
    messages, 
    conversations,
    publishedConversations, // NEW: Published conversations
    loading: conversationLoading,
    isProcessing,
    addMessage, 
    currentConversationId,
    startNewConversation,
    switchToConversation,
    addLocalMessage, // NEW: For adding frontend-generated messages
    localGradeState, // Local grade processing state
    gradeProcessor, // NEW: For matrix generation
    pendingConversationId,
    addExternalConversations, // Admin: Add external conversations
    // +Human feature
    assignStarGrade,
    deleteStarGrade,
    resumeFromPause,
    canResume,
    isResuming,
    txtPaused,
    txtPausePhase,
    txtModelGradesReceived,
    txtIonoffGradeReceived,
    txtResumeMessageId,
    // Published conversations feature
    publishCurrentConversation,
    unpublishCurrentConversation,
    forkAndSendMessage,
    // Right to Privacy
    right2Privacy,
  } = useConversation(user);

  // 💰 Ionoff cost calculation (OR sub-key delta vs header fallback)
  const {
    onPromptSent: ionoffCostOnPromptSent,
    onPromptStopped: ionoffCostOnPromptStopped,
    onPromptComplete: ionoffCostOnPromptComplete,
    calculateIonoffCost,
    persistIonoffCost,
    onCoachingCostReceived,
  } = useIonoffCost(currentConversationId);

  // 💰 IONOFF COST: Detect ionoff responses and calculate their cost
  // Tracks which messages we've already processed to avoid re-calculation
  const processedIonoffCosts = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // Find ionoff responses that need cost calculation
    const ionoffResponses = messages.filter(m => 
      m.isSynthesized === true && 
      !m.synthesisVariant && // Only the main ionoff response, not variants
      !m.costCalculated && // Not already calculated
      m.userPromptMessageId && // Has a linked prompt
      !processedIonoffCosts.current.has(m.id) // Not currently processing
    );
    
    if (ionoffResponses.length === 0) return;
    
    // Process each ionoff response that needs cost calculation
    for (const ionoffMsg of ionoffResponses) {
      // Mark as processing to avoid duplicate calculations
      processedIonoffCosts.current.add(ionoffMsg.id);
      
      console.log('💰 Detected ionoff response needing cost calculation', {
        ionoffMessageId: ionoffMsg.id,
        promptId: ionoffMsg.userPromptMessageId,
      });
      
      // Calculate and persist the cost (async, non-blocking)
      (async () => {
        try {
          const result = await calculateIonoffCost(ionoffMsg, messages);
          
          if (result) {
            await persistIonoffCost(ionoffMsg.id, result.cost, result.method);
            
            // Mark prompt as complete for tracking
            if (ionoffMsg.userPromptMessageId) {
              ionoffCostOnPromptComplete(ionoffMsg.userPromptMessageId);
            }
          }
        } catch (error) {
          console.error('Failed to calculate ionoff cost', error);
          // Remove from processed set so it can be retried
          processedIonoffCosts.current.delete(ionoffMsg.id);
        }
      })();
    }
  }, [messages, calculateIonoffCost, persistIonoffCost, ionoffCostOnPromptComplete]);
  
  // Reset processed costs when conversation changes
  useEffect(() => {
    processedIonoffCosts.current.clear();
  }, [currentConversationId]);

  // 🎯 Reset bottom section collapsed state when starting a new conversation
  // This ensures the prompt box is visible when the user creates a new conversation
  useEffect(() => {
    if (messages.length === 0 && isBottomSectionCollapsed) {
      setIsBottomSectionCollapsed(false);
    }
  }, [messages.length, isBottomSectionCollapsed]);
  
  // Expose state to window for headless browser testing (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).__ionoffGetProcessingState = () => ({
        isProcessing,
        messageCount: messages.length,
        lastMessageType: messages.length > 0 ? {
          isSynthesized: messages[messages.length - 1].isSynthesized,
          isGradeMatrix: messages[messages.length - 1].isGradeMatrix,
          isCostSummary: messages[messages.length - 1].isCostSummary,
          role: messages[messages.length - 1].role,
          modelUsed: messages[messages.length - 1].modelUsed,
        } : null,
        localGradeState: localGradeState ? {
          hasGrades: localGradeState.thisConversationCombined.modelGrades.size > 0 || localGradeState.selfEvalCombined.size > 0,
          gradeCount: localGradeState.thisConversationCombined.modelGrades.size + localGradeState.selfEvalCombined.size,
          isComplete: false, // No longer tracked at this level
        } : null,
        conversationId: currentConversationId,
      });
      
      (window as any).__ionoffStartNewConversation = () => {
        startNewConversation();
      };
      
      return () => {
        delete (window as any).__ionoffGetProcessingState;
        delete (window as any).__ionoffStartNewConversation;
      };
    }
  }, [isProcessing, messages, localGradeState, currentConversationId, startNewConversation]);
  const { allModels, loading: modelLoading, error: modelError } = useModelSelection();
  
  // LCS Highlighting state (shared across layout)
  const highlighting = useHighlighting();
  
  // Find candidate messages for highlighting chips
  const candidateModels = useMemo(() => {
    const candidates = messages.filter(
      m => m.role === 'assistant' &&
           !m.isSynthesized &&
           !m.isCostSummary &&
           !m.isGradeMatrix &&
           !m.isGradingResponse &&
           !m.isExtractionResponse &&
           m.modelUsed !== 'ionoff-debug' &&
           m.modelUsed !== 'ionoff-system' &&
           m.content &&
           m.content.length > 50
    );
    return candidates.map(c => c.modelUsed).filter((m): m is string => !!m);
  }, [messages]);

  // Assign fixed colors to selected models (from the start, not just candidates)
  // This is for header chips and should update with slider changes
  const [modelColorAssignments, setModelColorAssignments] = useState<Map<string, string>>(new Map());
  
  // Separate color assignments for dots - based on LAST SENT MESSAGE, not current slider
  // This prevents dots from changing color when slider moves before hitting send
  const [dotColorAssignments, setDotColorAssignments] = useState<Map<string, string>>(new Map());
  
  useEffect(() => {
    const assignColors = async () => {
      const assignments = new Map<string, string>();
      const colors = highlighting.HIGHLIGHTER_COLORS;
      
      for (let index = 0; index < selectedModels.length && index < colors.length; index++) {
        const openrouterId = selectedModels[index];
        const colorHex = colors[index].hex;
        
        // Get displayedModelId from modelIdService
        try {
          const displayedModelId = await getDisplayedModelID(openrouterId);
          
          // Map displayedModelId to color (base case)
          assignments.set(displayedModelId, colorHex);
          
          // Map ALL possible reasoning/effort suffixes for ALL models
          // The backend adds these dynamically based on actual usage, so we need all combinations
          const suffixes = ['(high)', '(low)', '(medium)', '(default)', '(reasoning)'];
          for (const suffix of suffixes) {
            assignments.set(`${displayedModelId} ${suffix}`, colorHex);
          }
        } catch (error) {
          console.error(`Failed to get displayedModelID for ${openrouterId}:`, error);
        }
        
        // Fallback: Also map openrouterId for backward compatibility
        assignments.set(openrouterId, colorHex);
        assignments.set(`${openrouterId} (high)`, colorHex);
        assignments.set(`${openrouterId} (low)`, colorHex);
      }
      
      setModelColorAssignments(assignments);
    };
    
    assignColors();
  }, [selectedModels, highlighting.HIGHLIGHTER_COLORS]);
  
  // Update dot colors ONLY when a new user message is sent
  // This keeps dots stable when user moves slider before sending
  useEffect(() => {
    // Find the most recent user message with sendConfig
    const lastUserMessage = [...messages]
      .reverse()
      .find(m => m.role === 'user' && m.sendConfig);
    
    if (!lastUserMessage?.sendConfig?.selectedModels) {
      // No messages sent yet - use current modelColorAssignments as fallback
      setDotColorAssignments(new Map(modelColorAssignments));
      return;
    }
    
    // Build color assignments from the SENT message's selectedModels
    const assignDotColors = async () => {
      const assignments = new Map<string, string>();
      const colors = highlighting.HIGHLIGHTER_COLORS;
      const sentModels = lastUserMessage.sendConfig!.selectedModels.map(m => m.openrouterId);
      
      for (let index = 0; index < sentModels.length && index < colors.length; index++) {
        const openrouterId = sentModels[index];
        const colorHex = colors[index].hex;
        
        try {
          const displayedModelId = await getDisplayedModelID(openrouterId);
          assignments.set(displayedModelId, colorHex);
          assignments.set(`${displayedModelId} (high)`, colorHex);
          assignments.set(`${displayedModelId} (low)`, colorHex);
          
          if (displayedModelId.toLowerCase().includes('reasoning')) {
            const withReasoningSuffix = `${displayedModelId} (reasoning)`;
            assignments.set(withReasoningSuffix, colorHex);
            assignments.set(`${withReasoningSuffix} (high)`, colorHex);
            assignments.set(`${withReasoningSuffix} (low)`, colorHex);
          }
        } catch (error) {
          console.error(`Failed to get displayedModelID for ${openrouterId}:`, error);
        }
        
        assignments.set(openrouterId, colorHex);
        assignments.set(`${openrouterId} (high)`, colorHex);
        assignments.set(`${openrouterId} (low)`, colorHex);
      }
      
      setDotColorAssignments(assignments);
      console.log('🎨 Updated dot colors from last sent message', {
        sentModelsCount: sentModels.length,
        messageId: lastUserMessage.id.substring(0, 8)
      });
    };
    
    assignDotColors();
  }, [messages, highlighting.HIGHLIGHTER_COLORS, modelColorAssignments]);
  
  // Get ordered colors for multi-color ionoff logo
  // Always reflects current slider selection so logo updates dynamically in all views
  const tagColors = useMemo(() => {
    // Filter out inactive models to show only active models in logo
    const activeModels = selectedModels.filter(id => !inactiveModels.has(id));
    return activeModels
      .map(modelId => modelColorAssignments.get(modelId))
      .filter((c): c is string => !!c);
  }, [selectedModels, modelColorAssignments, inactiveModels]);

  // Create set of valid OpenRouter model IDs (for filtering leaderboards)
  // Only models that are choosable from the header search should appear in peer-review stacks
  const validOpenRouterModelIds = useMemo(() => {
    return new Set(allOpenRouterModels.map(model => model.id));
  }, [allOpenRouterModels]);

  // Handle model swaps from retry/fallback logic
  useEffect(() => {
    // Find the most recent model swap message
    const modelSwapMessage = messages
      .filter(m => m.modelSwap)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
    
    if (modelSwapMessage && modelSwapMessage.modelSwap) {
      const { removed, added } = modelSwapMessage.modelSwap;
      
      // Check if we need to apply this swap
      if (selectedModels.includes(removed) && !selectedModels.includes(added)) {
        console.log('🔄 Applying model swap:', { removed, added });
        
        setSelectedModels(prev => {
          const newModels = [...prev];
          const removedIndex = newModels.indexOf(removed);
          if (removedIndex !== -1) {
            // Replace the failed model with the fallback at the same position
            newModels[removedIndex] = added;
          }
          return newModels;
        });
      }
    }
  }, [messages, selectedModels]);

  // Debug logging - TEMPORARILY DISABLED
  // console.log('ChatLayout - messages:', messages);
  // console.log('ChatLayout - conversations:', conversations);
  // console.log('ChatLayout - user:', user);
  // console.log('ChatLayout - conversationLoading:', conversationLoading);
  // console.log('ChatLayout - currentConversationId:', currentConversationId);
  // console.log('ChatLayout - allModels:', allModels);
  // console.log('ChatLayout - modelLoading:', modelLoading);
  // console.log('ChatLayout - modelError:', modelError);
  // console.log('ChatLayout - targetCount:', targetCount);
  // console.log('ChatLayout - prioritize:', prioritize);
  // console.log('ChatLayout - selectedModels:', selectedModels);
  // console.log('ChatLayout - slider disabled?', !currentConversationId);

  // Automatically sign in anonymously when component mounts
  useEffect(() => {
    if (!user && !authLoading) {
      signInAnonymously().catch(console.error);
    }
  }, [user, authLoading, signInAnonymously]);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme');
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const enableDark = stored ? stored === 'dark' : prefersDark;
      setIsDarkMode(enableDark);
      const root = document.documentElement;
      if (enableDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    } catch (e) {
      // no-op
    }
  }, []);

  // Fetch cost tiers from Firestore
  useEffect(() => {
    const fetchCostTiers = async () => {
      try {
        const tiers = await CostTiersService.getCostTiers();
        setCostTiers(tiers);
      } catch (error) {
        console.error('Error fetching cost tiers:', error);
      }
    };
    fetchCostTiers();
  }, []);

  // Fetch reasoning effort for selected models from Firestore
  useEffect(() => {
    const fetchReasoningEffortForSelectedModels = async () => {
      if (selectedModels.length === 0) return;
      
      try {
        const modelListsDoc = await getDoc(doc(db, 'system', 'modelLists'));
        
        if (modelListsDoc.exists()) {
          const allData = modelListsDoc.data();
          const balancedData = allData?.balanced;
          const balancedModelsList = balancedData?.models || [];
          
          // Create map of reasoning effort for selected models
          const reasoningMap = new Map<string, string>();
          balancedModelsList.forEach((model: any) => {
            if (model.openrouterId && model.defaultReasoningEffort) {
              reasoningMap.set(model.openrouterId.toLowerCase(), model.defaultReasoningEffort);
            }
          });
          
          // Update allOpenRouterModels to include selected models with reasoning effort
          setAllOpenRouterModels(prev => {
            const existingMap = new Map(prev.map(m => [m.id.toLowerCase(), m]));
            
            // Add or update selected models with reasoning effort
            selectedModels.forEach(modelId => {
              const lowerCaseId = modelId.toLowerCase();
              const existing = existingMap.get(lowerCaseId);
              const reasoningEffort = reasoningMap.get(lowerCaseId);
              
              if (reasoningEffort || !existing) {
                existingMap.set(lowerCaseId, {
                  id: modelId,
                  name: existing?.name || modelId,
                  combinedRank: existing?.combinedRank || null,
                  defaultReasoningEffort: reasoningEffort || existing?.defaultReasoningEffort || null
                });
              }
            });
            
            return Array.from(existingMap.values());
          });
        }
      } catch (error) {
        console.warn('Error fetching reasoning effort for selected models:', error);
      }
    };
    
    fetchReasoningEffortForSelectedModels();
  }, [selectedModels]);

  // Fetch pricing for selected models from OpenRouter API
  useEffect(() => {
    const fetchModelPricing = async () => {
      if (selectedModels.length === 0) {
        setModelPricing(new Map());
        return;
      }

      try {
        const pricingMap = new Map<string, {prompt: number; completion: number}>();
        
        // Fetch all models from OpenRouter (one API call)
        const response = await fetch('https://openrouter.ai/api/v1/models', {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENROUTER_API_KEY || ''}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const allModels = data.data || [];

          // Extract pricing for each selected model
          selectedModels.forEach(modelId => {
            // Remove :online suffix if present
            const baseModelId = modelId.replace(/:online$/, '');
            const model = allModels.find((m: any) => 
              m.id.toLowerCase() === baseModelId.toLowerCase()
            );

            if (model?.pricing) {
              pricingMap.set(modelId, {
                prompt: parseFloat(model.pricing.prompt) || 0,
                completion: parseFloat(model.pricing.completion) || 0
              });
            }
          });
        }

        setModelPricing(pricingMap);
      } catch (error) {
        console.error('Error fetching model pricing:', error);
      }
    };

    fetchModelPricing();
  }, [selectedModels]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      const root = document.documentElement;
      if (next) {
        root.classList.add('dark');
        try { localStorage.setItem('theme', 'dark'); } catch (_e) {}
      } else {
        root.classList.remove('dark');
        try { localStorage.setItem('theme', 'light'); } catch (_e) {}
      }
      return next;
    });
  };

  const ThemeToggle: React.FC = () => (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleTheme}
      className="h-9 w-9 p-0 bg-background border border-border shadow-sm rounded-full hover:bg-muted transition-colors"
      title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDarkMode ? (
        <Sun className="h-5 w-5" />
      ) : (
        <Moon className="h-5 w-5" />
      )}
    </Button>
  );

  // Automatically expand top section when first message is sent (but not if user manually collapsed it)
  // Header section defaults to collapsed and only expands when user manually toggles
  // Status section (coaching, prompts, dots) is always visible when not in new conversation state

  // Measure header height for mobile floating controls positioning
  useEffect(() => {
    if (!isTopSectionCollapsed && headerSectionRef.current) {
      setHeaderHeight(headerSectionRef.current.offsetHeight);
    } else {
      setHeaderHeight(0);
    }
  }, [isTopSectionCollapsed, isPeerReviewSettingsExpanded, selectedModels.length]);

  // Calculate how many mobile triplet controls can fit without overlapping centered dots
  useEffect(() => {
    const calculateVisibleControls = () => {
      if (typeof window === 'undefined' || window.innerWidth >= 1024) return; // Only for mobile
      
      const screenWidth = window.innerWidth;
      const dotCount = selectedModels.length + 1; // AI models + ionoff
      
      // Dot sizing (from ResponseDots.tsx)
      const regularDotSize = 12; // 0.75rem
      const ionoffDotSize = 16; // 1.0rem
      const dotGap = 12; // gap-3
      const dotsWidth = (dotCount - 1) * regularDotSize + ionoffDotSize + (dotCount - 1) * dotGap;
      
      // Control sizing
      const leftPadding = 16; // px-4
      const buffer = 12; // safety margin
      const controlSize = 36; // h-9 w-9
      const controlGap = 8; // gap-2
      const expanderWidth = controlSize + controlGap; // "..." button width + gap
      
      // Calculate available space (left edge of centered dots minus padding and buffer)
      const dotsLeftEdge = screenWidth / 2 - dotsWidth / 2;
      const availableWidth = dotsLeftEdge - leftPadding - buffer;
      
      // Determine how many controls fit (need room for expander if not showing all 3)
      if (availableWidth >= 3 * controlSize + 2 * controlGap) {
        setVisibleMobileControlsCount(3); // All fit, no expander needed
      } else if (availableWidth >= 2 * controlSize + controlGap + expanderWidth) {
        setVisibleMobileControlsCount(2);
      } else if (availableWidth >= controlSize + expanderWidth) {
        setVisibleMobileControlsCount(1);
      } else {
        setVisibleMobileControlsCount(0); // Just show expander
      }
    };
    
    calculateVisibleControls();
    window.addEventListener('resize', calculateVisibleControls);
    return () => window.removeEventListener('resize', calculateVisibleControls);
  }, [selectedModels.length]);

  // Clear cancellation flag when a new user message is sent
  useEffect(() => {
    if (!messages.length || !currentConversationId) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === 'user') {
      // Clear cancellation flag when new message is sent
      ConversationService.clearCancellationFlag(currentConversationId).catch(console.error);
    }
  }, [messages, currentConversationId]);

  // Handle dot click - scroll to message and highlight it
  const handleDotClick = useCallback((messageId: string) => {
    // Find the message element
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      const scrollParent = messageElement.closest('[data-scroll-container="chat"]') as HTMLElement | null;

      if (scrollParent) {
        const paddingOffset = 12;
        const targetTop = Math.max(messageElement.offsetTop - paddingOffset, 0);
        scrollParent.scrollTo({ top: targetTop, behavior: 'auto' });
      } else {
        // Fallback to default behavior if container not found
        messageElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
      }

      // Highlight the message
      setHighlightedMessageId(messageId);

      // Remove highlight after 2 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2000);
    }
  }, []);

  // Contribute modal state
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false);
  const [contributionNotification, setContributionNotification] = useState<{
    type: 'success' | 'cancelled';
    message: string;
  } | null>(null);

  // State to track if we're processing Stripe sign-in
  const [isProcessingStripeSignIn, setIsProcessingStripeSignIn] = useState(false);

  // Handle contribution return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contributionStatus = urlParams.get('contribution');
    const stripeSessionId = urlParams.get('stripe_session_id');
    
    if (contributionStatus === 'success') {
      // Clear URL parameters first
      window.history.replaceState({}, '', window.location.pathname);
      
      // If we have a session ID, auto sign-in
      if (stripeSessionId && !isProcessingStripeSignIn) {
        setIsProcessingStripeSignIn(true);
        
        // Import and call the sign-in function
        const completeStripeAuth = async () => {
          try {
            console.log('🔐 [Stripe] Completing sign-in with session:', stripeSessionId);
            
            // Get the anonymous UID before signing in (for account linking)
            const anonymousUid = getAnonymousUidForLinking();
            
            // Call backend to get custom token
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const completeSignIn = httpsCallable(functions, 'completeStripeSignIn');
            
            const result = await completeSignIn({ sessionId: stripeSessionId });
            const data = result.data as { success: boolean; customToken?: string; email?: string; uid?: string };
            
            if (data.success && data.customToken) {
              // Sign in with custom token
              await signInWithToken(data.customToken);
              
              // Link anonymous account if needed
              if (anonymousUid && anonymousUid !== data.uid) {
                console.log('🔗 [Stripe] Linking anonymous account:', anonymousUid);
                try {
                  const linkAccount = httpsCallable(functions, 'linkAnonymousAccount');
                  await linkAccount({ anonymousUid });
                } catch (linkErr) {
                  console.error('🔗 [Stripe] Account linking error (non-fatal):', linkErr);
                }
                clearAnonymousUidForLinking();
              }
              
              // Invalidate balance cache since we just paid
              invalidateBalanceCache();
              
              setContributionNotification({
                type: 'success',
                message: `Welcome! Signed in as ${data.email}`,
              });
              
              // Check for pending prompt - store for deferred send when component is ready
              const pending = getPendingPrompt();
              if (pending) {
                console.log('📝 [Stripe] Found pending prompt, storing for deferred send');
                setDeferredPendingPrompt({
                  prompt: pending.prompt,
                  plusHuman: pending.plusHuman,
                  source: 'stripe'
                });
              }
            } else {
              setContributionNotification({
                type: 'success',
                message: 'Thank you! Your contribution has been received.',
              });
            }
          } catch (err) {
            console.error('🔐 [Stripe] Sign-in error:', err);
            setContributionNotification({
              type: 'success',
              message: 'Thank you! Your contribution has been received. Please sign in to continue.',
            });
          } finally {
            setIsProcessingStripeSignIn(false);
          }
        };
        
        completeStripeAuth();
      } else {
        setContributionNotification({
          type: 'success',
          message: 'Thank you! Your contribution has been received.',
        });
      }
      
      // Auto-dismiss after 5 seconds
      setTimeout(() => setContributionNotification(null), 5000);
    } else if (contributionStatus === 'cancelled') {
      setContributionNotification({
        type: 'cancelled',
        message: 'Contribution cancelled. You can try again anytime.',
      });
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-dismiss after 3 seconds
      setTimeout(() => setContributionNotification(null), 3000);
      // Clear pending prompt since they cancelled
      clearPendingPrompt();
    }
    
    // Handle magic link sign-in return
    const magiclinkStatus = urlParams.get('magiclink');
    if (magiclinkStatus === 'success') {
      console.log('🔐 [MagicLink] Sign-in complete, checking for pending prompt');
      
      // Clear URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      
      // Check for pending prompt - store it for sending when component is ready
      const pending = getPendingPrompt();
      if (pending) {
        console.log('📝 [MagicLink] Found pending prompt, storing for deferred send');
        // Store in state - will be sent by useEffect when component is ready
        setDeferredPendingPrompt({
          prompt: pending.prompt,
          plusHuman: pending.plusHuman,
          source: 'magiclink'
        });
        setContributionNotification({
          type: 'success',
          message: 'Signed in! Sending your prompt...',
        });
        setTimeout(() => setContributionNotification(null), 5000);
      } else {
        // No pending prompt - just show welcome message
        setContributionNotification({
          type: 'success',
          message: 'Welcome back! You are now signed in.',
        });
        setTimeout(() => setContributionNotification(null), 3000);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pending message data for auth/payment flow
  const [pendingMessageData, setPendingMessageData] = useState<{
    content: string;
    plusHumanContent?: string;
    options?: SendMessageOptions;
  } | null>(null);

  // Deferred pending prompt - stored when returning from auth flow (Stripe or magic link)
  // Will be sent when component is ready (user authenticated + models loaded)
  // This prevents race conditions where handleSendMessage is called before component is initialized
  const [deferredPendingPrompt, setDeferredPendingPrompt] = useState<{
    prompt: string;
    plusHuman?: string;
    source: 'stripe' | 'magiclink';
  } | null>(null);

  // LoginModal removed - ContributeModal now handles both add funds and sign-in

  // EUMPWU balance check state
  const [isInsufficientFundsModalOpen, setIsInsufficientFundsModalOpen] = useState(false);
  const [userBalanceInfo, setUserBalanceInfo] = useState<UserBalanceResult | null>(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);

  // Honor system modal state and balance data (legacy)
  const [isHonorSystemExpanded, setIsHonorSystemExpanded] = useState(false);
  const [balanceData, setBalanceData] = useState<BalanceData | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  // Coopital voting modal state: null = closed, 'tryBeforePay' or 'marginRate' = which ballot
  const [coopitalVotingBallot, setCoopitalVotingBallot] = useState<'tryBeforePay' | 'marginRate' | null>(null);

  // EUMPWU: User's personal balance state with fee transparency
  const [personalBalance, setPersonalBalance] = useState<{
    balance: number;
    limit: number;
    usage: number;
    hasKey: boolean;
    totalPaid: number; // What they actually paid (gross)
    totalStripeFees: number; // Stripe processing fees (2.9% + $0.30)
    totalOpenRouterTax: number; // OpenRouter MD sales tax (3%)
    totalOpenRouterCosts: number; // AI usage costs
    // Coopital fields
    coopitalShare: number;
    coopitalValue: number;
    coopitalMarginRate: number;
    tryBeforePayThreshold: number;
    userShareOfSystemCosts: number;
    userShareOfTryBeforePayCosts: number;
    systemTotalAiUsage: number;
    userAiUsage: number;
    openRouterManagementRemaining: number | null;
    openRouterManagementDelta: number | null;
    openRouterManagementUpdatedAt: string | null;
  } | null>(null);

  const loadPersonalBalance = useCallback(async () => {
    if (!user?.uid || !isAuthenticatedWithEmail()) return;
    try {
      const result = (await checkUserBalance()) as any;
      if (result.success) {
        setPersonalBalance({
          balance: result.balance,
          limit: result.limit,
          usage: result.usage,
          hasKey: result.hasKey,
          totalPaid: result.totalPaid || result.limit || 0,
          totalStripeFees: result.totalStripeFees || 0,
          totalOpenRouterTax: result.totalOpenRouterTax || 0,
          totalOpenRouterCosts: result.totalOpenRouterCosts || result.usage || 0,
          coopitalShare: result.coopitalShare || 0,
          coopitalValue: result.coopitalValue || 0,
          coopitalMarginRate: result.coopitalMarginRate || 0,
          tryBeforePayThreshold: result.tryBeforePayThreshold || 0,
          userShareOfSystemCosts: result.userShareOfSystemCosts || 0,
          userShareOfTryBeforePayCosts: result.userShareOfTryBeforePayCosts || 0,
          systemTotalAiUsage: result.systemTotalAiUsage || 0,
          userAiUsage: result.userAiUsage || 0,
          openRouterManagementRemaining:
            typeof result.openRouterManagementRemaining === 'number'
              ? result.openRouterManagementRemaining
              : null,
          openRouterManagementDelta:
            typeof result.openRouterManagementDelta === 'number'
              ? result.openRouterManagementDelta
              : null,
          openRouterManagementUpdatedAt:
            typeof result.openRouterManagementUpdatedAt === 'string'
              ? result.openRouterManagementUpdatedAt
              : null,
        });
      }
    } catch (error) {
      console.error('Error fetching personal balance:', error);
    }
  }, [user?.uid]);

  // Subscribe to balance data for honor system button (legacy)
  useEffect(() => {
    const unsubscribe = subscribeToSystemBalance(
      user?.uid,
      (data) => {
        setBalanceData(data);
        setBalanceError(null);
      },
      (err) => {
        console.error('Balance subscription error:', err);
        setBalanceError('Unable to load balance data');
      }
    );
    return unsubscribe;
  }, [user?.uid]);

  // EUMPWU: Fetch user's personal OpenRouter balance with fee details
  useEffect(() => {
    if (!user?.uid || !isAuthenticatedWithEmail()) return;
    void loadPersonalBalance();
    const interval = setInterval(() => void loadPersonalBalance(), 60000);
    return () => clearInterval(interval);
  }, [user?.uid, loadPersonalBalance]);

  // Handle contribute button click - opens modal
  const handleContributeClick = useCallback(() => {
    setIsContributeModalOpen(true);
  }, []);

  // 🎯 PHASE 1: Stop a specific prompt (not conversation-wide)
  const handlePromptStop = async (promptMessageId: string) => {
    if (!currentConversationId) return;
    try {
      console.log('⚡ Soft stop: Immediately marking prompt as stopped', { promptMessageId });

      // 💰 IONOFF COST: Mark this prompt for fallback cost calculation (delta unreliable after stop)
      ionoffCostOnPromptStopped(promptMessageId);

      // 🎯 CRITICAL: Update Firestore IMMEDIATELY to stop animation
      // Set both cancellation flag AND stoppedBeforeEOP in parallel
      const conversationRef = doc(db, 'conversations', currentConversationId);
      const messageRef = doc(db, 'conversations', currentConversationId, 'messages', promptMessageId);
      
      await Promise.all([
        // Set per-prompt cancellation flag for backend to check
        updateDoc(conversationRef, {
          [`promptCancellations.${promptMessageId}.cancelProcessing`]: true,
          [`promptCancellations.${promptMessageId}.cancelRequestedAt`]: serverTimestamp(),
        }),
        // Immediately mark message as stopped (stops animation instantly via Firestore listener)
        updateDoc(messageRef, {
          stoppedBeforeEOP: true,
          stoppedAt: serverTimestamp(),
          useFallbackCost: true, // Mark for fallback cost calculation
        })
      ]);
      
      // Add stop message as 'assistant' role to avoid triggering processUserMessage
      await ConversationService.addMessage(currentConversationId, {
        role: 'assistant',
        content: '🛑 **Stop requested** - Cancelling processing to save costs',
        isSynthesized: false,
        modelUsed: 'ionoff-system'
      });

      console.log('✅ Prompt stop requested', { promptMessageId });
    } catch (error) {
      console.error('Failed to stop prompt processing:', error);
    }
  };

  // Scroll to a specific prompt and highlight it
  const handleScrollToPrompt = useCallback((promptMessageId: string) => {
    const messageElement = document.getElementById(`message-${promptMessageId}`);
    if (messageElement) {
      const scrollParent = messageElement.closest('[data-scroll-container="chat"]') as HTMLElement | null;
      
      if (scrollParent) {
        const paddingOffset = 12;
        const targetTop = Math.max(messageElement.offsetTop - paddingOffset, 0);
        scrollParent.scrollTo({ top: targetTop, behavior: 'smooth' });
      } else {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }

      // Highlight the message
      setHighlightedMessageId(promptMessageId);

      // Remove highlight after 2 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2000);
    }
  }, []);

  // LEGACY: Keep old handleStopProcessing for backward compatibility
  // TODO: Remove once all call sites are updated
  const handleStopProcessing = async () => {
    if (!currentConversationId) return;
    try {
      // Set cancellation flag
      await ConversationService.cancelProcessing(currentConversationId);
      
      // Immediately clear processing flag (don't wait for backend)
      // This ensures the stop button changes to send button right away
      await ConversationService.clearProcessingFlag(currentConversationId);
      
      // Add stop message as 'assistant' role to avoid triggering processUserMessage
      // This prevents race conditions and unnecessary function invocations
      await ConversationService.addMessage(currentConversationId, {
        role: 'assistant',
        content: '🛑 **Stop requested** - Cancelling processing to save costs',
        isSynthesized: false,
        modelUsed: 'ionoff-system'
      });
    } catch (error) {
      console.error('Failed to stop processing:', error);
    }
  };


  // Track if we're switching conversations to prevent race conditions
  const isSwitchingConversationRef = React.useRef(false);
  
  // Track if change is user-initiated vs Firestore update (for peer review settings persistence)
  const isUserInitiatedChangeRef = React.useRef(false);

  // Combined listener for user profile fields (customModels, dismissedIcebreakers, peer review settings)
  useEffect(() => {
    if (!user) {
      setCustomModels([]);
      setHasCustomized(false);
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      // Skip ALL updates if this came from a user-initiated change (to avoid circular updates)
      if (isUserInitiatedChangeRef.current) {
        return;
      }

      if (snapshot.exists()) {
        const data = snapshot.data();
        
        // Update customModels
        const savedCustomModels = data?.customModels ?? [];
        setCustomModels(savedCustomModels);
        // If user has custom models, they have customized
        if (savedCustomModels.length > 0) {
          setHasCustomized(true);
        }
        
        // Update dismissedIcebreakers
        const savedDismissedIcebreakers = data?.dismissedIcebreakers ?? [];
        setDismissedIcebreakers(savedDismissedIcebreakers);
        
        // Load user-level peer review settings (prioritize, targetCount, peerReviewLevel)
        // These persist at userId level, not conversation level
        if (data?.peerReviewSettings) {
          const prs = data.peerReviewSettings;
          if (prs.prioritize) {
            setPrioritize(prs.prioritize);
          }
          if (prs.targetCount !== undefined) {
            setTargetCount(prs.targetCount);
          }
          // Restore peer review level from individual toggle states
          if (prs.selfEvalEnabled !== undefined) {
            setSelfEvalEnabled(prs.selfEvalEnabled);
          }
          if (prs.txtEnabled !== undefined) {
            setTxtEnabled(prs.txtEnabled);
          }
          if (prs.plusHumanEnabled !== undefined) {
            setPlusHumanEnabled(prs.plusHumanEnabled);
          }
        }
      } else {
        // User document doesn't exist yet - use defaults
        setCustomModels([]);
        setHasCustomized(false);
        setDismissedIcebreakers([]);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Simple handler for prompt settings toggle (no persistence - resets each new prompt)
  const handleToolsExpandedChange = useCallback((newValue: boolean) => {
    setToolsExpanded(newValue);
  }, []);
  
  // Save peer review settings to user profile when they change
  const savePeerReviewSettings = useCallback(async () => {
    if (!user) return;
    
    isUserInitiatedChangeRef.current = true;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      const peerReviewSettings = {
        prioritize,
        targetCount,
        selfEvalEnabled,
        txtEnabled,
        plusHumanEnabled,
      };
      
      if (userDoc.exists()) {
        await updateDoc(userRef, { peerReviewSettings });
      } else {
        await setDoc(userRef, { peerReviewSettings }, { merge: true });
      }
      
      // Reset flag after save
      setTimeout(() => {
        isUserInitiatedChangeRef.current = false;
      }, 500);
    } catch (error) {
      console.error('Error saving peer review settings:', error);
      isUserInitiatedChangeRef.current = false;
    }
  }, [user, prioritize, targetCount, selfEvalEnabled, txtEnabled, plusHumanEnabled]);
  
  // Debounced save for peer review settings - triggers when any setting changes
  useEffect(() => {
    // Don't save on initial mount or when loading from Firestore
    if (isUserInitiatedChangeRef.current) return;
    
    const timeoutId = setTimeout(() => {
      savePeerReviewSettings();
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(timeoutId);
  }, [prioritize, targetCount, selfEvalEnabled, txtEnabled, plusHumanEnabled, savePeerReviewSettings]);

  // Handler to dismiss an icebreaker (persist to user profile)
  const handleDismissIcebreaker = useCallback(async (icebreaker: string) => {
    if (!user) {
      console.log('No user logged in, cannot persist dismissed icebreaker');
      return;
    }
    
    // Optimistically update local state
    setDismissedIcebreakers(prev => {
      if (prev.includes(icebreaker)) return prev; // Already dismissed
      return [...prev, icebreaker];
    });
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      const currentDismissed = userDoc.exists() 
        ? (userDoc.data()?.dismissedIcebreakers ?? []) 
        : [];
      
      // Only add if not already in the list
      if (!currentDismissed.includes(icebreaker)) {
        const newDismissed = [...currentDismissed, icebreaker];
        
        if (userDoc.exists()) {
          await updateDoc(userRef, { dismissedIcebreakers: newDismissed });
        } else {
          await setDoc(userRef, { dismissedIcebreakers: newDismissed }, { merge: true });
        }
        
        console.log('✅ Dismissed icebreaker saved to profile:', icebreaker.substring(0, 30) + '...');
      }
    } catch (error) {
      console.error('Error saving dismissed icebreaker:', error);
      // Revert optimistic update on error
      setDismissedIcebreakers(prev => prev.filter(i => i !== icebreaker));
    }
  }, [user]);

  // Sync only selectedModels and inactiveModels with conversation
  // Peer review settings (targetCount, prioritize) persist at userId level, not conversation level
  useEffect(() => {
    if (!currentConversationId) {
      return;
    }

    const conversationRef = doc(db, 'conversations', currentConversationId);
    const unsubscribe = onSnapshot(conversationRef, (snapshot) => {
      // Skip ALL updates if we're switching conversations to avoid race conditions
      if (isSwitchingConversationRef.current) {
        return;
      }

      if (snapshot.exists()) {
        const data = snapshot.data();
        // Only sync model selection, not peer review settings
        const conversationSelectedModels = data?.modelParameters?.selectedModels ?? [];
        const conversationInactiveModels = data?.modelParameters?.inactiveModels ?? [];
        setSelectedModels(conversationSelectedModels);
        setInactiveModels(new Set(conversationInactiveModels));
        console.log('🔄 Synced models from conversation:', { 
          selectedModels: conversationSelectedModels,
          inactiveModels: conversationInactiveModels
        });
      }
    });

    return () => unsubscribe();
  }, [currentConversationId]);

  // Fetch models from Firestore based on prioritize selection
  // Custom mode: Use saved custom list
  // Standard modes: Pure fetch from curated lists (no memory)
  // Extracted as useCallback so it can be called directly when needed
  const fetchModels = useCallback(async () => {
    console.log('🔄 [fetchModels] Starting fetch', { prioritize, targetCount, currentConversationId: currentConversationId?.substring(0, 20) });
    
    // Skip fetch if we're in the middle of a toggle operation
    if (isTogglingRef.current) {
      console.log('🔄 [fetchModels] Skipped - toggle in progress');
      return;
    }

    try {
      // CUSTOM MODE: Use stored custom list
      if (prioritize === 'custom') {
        const customSelection = customModels.slice(0, targetCount);
        setSelectedModels(customSelection);
        setModelListLastUpdated(null);
        
        console.log('🎨 Custom mode:', {
          totalCustom: customModels.length,
          targetCount,
          showing: customSelection.length
        });
        
        // Save to Firestore conversation only if one exists
        if (currentConversationId) {
          const conversationRef = doc(db, 'conversations', currentConversationId);
          await updateDoc(conversationRef, {
            'modelParameters.selectedModels': customSelection,
            'modelParameters.targetCount': targetCount,
            'modelParameters.prioritize': 'custom'
          });
        }
        return;
      }

      // STANDARD MODES: Pure fetch from curated Firestore lists
      const fieldMap: Record<string, string> = {
        'free': 'freeUnified',  // AA-ranked + HF-ranked free models
        'speed': 'speedy',      // Fastest output tokens/second
        'balance': 'balanced',  // Best combined intelligence/speed
        'value': 'value',       // Best combined intelligence/cost (IQ per dollar)
        'intelligence': 'top'   // Highest intelligence scores
      };

      const fieldName = fieldMap[prioritize];
      if (!fieldName) return;

      const modelListsRef = doc(db, 'system', 'modelLists');
      const modelListsDoc = await getDoc(modelListsRef);

      if (modelListsDoc.exists()) {
        const allData = modelListsDoc.data();
        const listData = allData?.[fieldName];
        const models = listData?.models || [];
        
        // Simple: Take first targetCount models from prioritized list
        const fetchedModels = models
          .slice(0, Math.min(targetCount, models.length))
          .map((model: any) => model.openrouterId)
          .filter(Boolean);
        
        console.log('🔄 [fetchModels] Standard mode fetch:', {
          prioritize,
          targetCount,
          fetched: fetchedModels.length,
          models: fetchedModels.slice(0, 3)
        });

        setSelectedModels(fetchedModels);
        // Handle both ISO string and Firestore Timestamp formats
        const rawLastUpdated = listData?.lastUpdated;
        const lastUpdatedStr = rawLastUpdated?.toDate
          ? rawLastUpdated.toDate().toISOString()
          : (typeof rawLastUpdated === 'string' ? rawLastUpdated : null);
        setModelListLastUpdated(lastUpdatedStr);
        console.log('🔄 [fetchModels] ✅ setSelectedModels called with', fetchedModels.length, 'models');
        
        // Save to Firestore conversation only if one exists
        if (currentConversationId) {
          const conversationRef = doc(db, 'conversations', currentConversationId);
          await updateDoc(conversationRef, {
            'modelParameters.selectedModels': fetchedModels,
            'modelParameters.targetCount': targetCount,
            'modelParameters.prioritize': prioritize
          });
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  }, [prioritize, targetCount, currentConversationId, customModels]);

  // Effect to run fetchModels when dependencies change
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Coopital: Gate check for anonymous/unpaid users changing settings
  // Returns true if the user should be blocked (gate shown)
  const requiresPaymentGate = useCallback((): boolean => {
    if (!isAuthenticatedWithEmail() || !personalBalance?.hasKey) {
      setIsContributeModalOpen(true);
      return true;
    }
    return false;
  }, [personalBalance?.hasKey]);

  // Update conversation's targetCount when slider changes
  const handleTargetCountChange = async (value: number[]) => {
    if (requiresPaymentGate()) return;
    console.log('🎚️ T Slider changed:', value);
    const newTargetCount = value[0];
    setTargetCount(newTargetCount);
    setIsAisSliderDragging(true); // Mark as dragging during value change
    
    if (!user) {
      console.warn('⚠️ User not authenticated yet');
      return;
    }

    try {
      const convId = currentConversationId;

      // Only update existing conversation, don't create a new one
      // New conversations will be created when user sends first message
      if (!convId) {
        // console.log(`📝 No conversation yet, targetCount=${newTargetCount} will be used when conversation is created`);
        return; // targetCount is already in state, will be used on conversation creation
      }

      // Update targetCount in existing conversation
      await updateDoc(doc(db, 'conversations', convId), {
        'modelParameters.targetCount': newTargetCount
      });
      console.log(`✅ Updated targetCount to ${newTargetCount}`);
    } catch (error) {
      console.error('Failed to update targetCount:', error);
    }
  };

  // Called when slider drag ends (value committed)
  const handleTargetCountCommit = () => {
    setIsAisSliderDragging(false);
  };

  // Get current peer review level based on toggle states
  const getPeerReviewLevel = (): 1 | 2 | 3 | 4 | 'custom' => {
    if (!plusHumanEnabled && !selfEvalEnabled && !txtEnabled) return 1;
    if (!plusHumanEnabled && !selfEvalEnabled && txtEnabled) return 2;
    if (!plusHumanEnabled && selfEvalEnabled && txtEnabled) return 3;
    if (plusHumanEnabled && selfEvalEnabled && txtEnabled) return 4;
    return 'custom';
  };
  
  const currentPeerReviewLevel = getPeerReviewLevel();

  // Update peer review settings when slider changes
  const handlePeerReviewLevelChange = (value: number[]) => {
    if (requiresPaymentGate()) return;
    const newLevel = value[0] + 1; // Slider is 0-indexed, levels are 1-indexed
    console.log('🔄 Peer Review Level changed:', newLevel);
    
    switch (newLevel) {
      case 1:
        setPlusHumanEnabled(false);
        setSelfEvalEnabled(false);
        setTxtEnabled(false);
        break;
      case 2:
        setPlusHumanEnabled(false);
        setSelfEvalEnabled(false);
        setTxtEnabled(true);
        break;
      case 3:
        setPlusHumanEnabled(false);
        setSelfEvalEnabled(true);
        setTxtEnabled(true);
        break;
      case 4:
        setPlusHumanEnabled(true);
        setSelfEvalEnabled(true);
        setTxtEnabled(true);
        break;
    }
  };

  // Update conversation's reasoningEffort when slider changes (kept for backward compat, now unused)
  const handleReasoningEffortChange = async (value: number[]) => {
    const effortLevels: ('low' | 'default' | 'high')[] = ['low', 'default', 'high'];
    const newReasoningEffort = effortLevels[value[0]];
    console.log('🧠 R Slider changed:', newReasoningEffort);
    setReasoningEffort(newReasoningEffort);
    
    if (!user) {
      console.warn('⚠️ User not authenticated yet');
      return;
    }

    try {
      const convId = currentConversationId;

      // Only update existing conversation, don't create a new one
      // New conversations will be created when user sends first message
      if (!convId) {
        // console.log(`📝 No conversation yet, reasoningEffort=${newReasoningEffort} will be used when conversation is created`);
        return; // reasoningEffort is already in state, will be used on conversation creation
      }

      // Update reasoningEffort in existing conversation
      await updateDoc(doc(db, 'conversations', convId), {
        'modelParameters.reasoningEffort': newReasoningEffort
      });
      console.log(`✅ Updated reasoningEffort to ${newReasoningEffort}`);
    } catch (error) {
      console.error('Failed to update reasoningEffort:', error);
    }
  };

  // 🎯 PHASE 2: Handle prompt editing - defined before handleSendMessage to avoid dependency issues
  const handleClearEdit = useCallback(() => {
    setEditingPromptId(null);
    setEditingPromptContent('');
  }, []);

  const handleSendMessage = useCallback(async (
    content: string,
    plusHumanContent?: string,
    options?: SendMessageOptions
  ) => {
    console.log('🔍 [handleSendMessage] Called', {
      user: !!user,
      isSending: isSendingMessageRef.current,
      selectedModelsCount: selectedModels.length,
      inactiveModelsCount: inactiveModels.size
    });
    
    if (!user) {
      console.error('🔍 [handleSendMessage] ❌ User not authenticated');
      return;
    }

    // 🔐 AUTH CHECK: Check balance (EUMPWU - Each User Must Pay What They Use)
    // Balance check works for both authenticated and anonymous users
    // JubileeMode (demo mode) returns canSend: true for everyone
    const hasEmailAuth = isAuthenticatedWithEmail();

    console.log('🔐 [handleSendMessage] Auth check:', { hasEmailAuth });

    if (!isCheckingBalance) {
      setIsCheckingBalance(true);
      try {
        const balanceResult = await checkUserBalance();
        setUserBalanceInfo(balanceResult);
        
        if (!balanceResult.canSend) {
          console.log('💰 [handleSendMessage] Cannot send - showing modal', balanceResult);
          setPendingMessageData({ content, plusHumanContent, options });
          // Store pending prompt before showing modal
          if (content) {
            storePendingPrompt(content, plusHumanContent);
          }
          setIsContributeModalOpen(true);
          setIsCheckingBalance(false);
          return;
        }
        console.log('💰 [handleSendMessage] Balance OK:', balanceResult.balance);

        // 🛡️ Enforce maxAis cap for anonymous try-before-pay users
        if (balanceResult.maxAis && targetCount > balanceResult.maxAis) {
          console.log(`🛡️ Capping AIs from ${targetCount} to ${balanceResult.maxAis} (anonymous user)`);
          setTargetCount(balanceResult.maxAis);
        }
      } catch (error) {
        console.error('💰 [handleSendMessage] Balance check error:', error);
        // On error, show contribute modal as a safe default
        setPendingMessageData({ content, plusHumanContent, options });
        if (content) {
          storePendingPrompt(content, plusHumanContent);
        }
        setIsContributeModalOpen(true);
        setIsCheckingBalance(false);
        return;
      } finally {
        setIsCheckingBalance(false);
      }
    }

    // Check if viewing someone else's published conversation
    if (currentConversationId) {
      const currentConversation = conversations.find(c => c.id === currentConversationId) || 
                                  publishedConversations.find(c => c.id === currentConversationId);
      
      if (currentConversation && currentConversation.published && currentConversation.userId !== user.uid) {
        console.log('🍴 Forking published conversation and sending message...');
        try {
          // Fork the conversation AND send the message - complete flow handled here
          await forkAndSendMessage(currentConversationId, content, options);
          console.log('✅ Fork + send complete');
          invalidateBalanceCache();
          window.setTimeout(() => void loadPersonalBalance(), 3000);
          isSendingMessageRef.current = false;
          return; // Exit early - fork handled everything
        } catch (error) {
          console.error('❌ Error forking conversation:', error);
          isSendingMessageRef.current = false;
          return;
        }
      }
    }

    // Prevent duplicate submissions (e.g., from double-clicking send button)
    if (isSendingMessageRef.current) {
      console.warn('🔍 [handleSendMessage] ⚠️ Message send already in progress, ignoring duplicate request');
      return;
    }

    isSendingMessageRef.current = true;
    console.log('🔍 [handleSendMessage] ✅ isSendingMessageRef set to true');

    try {
      // Filter out inactive models - only send active (non-disabled) models to backend
      const activeModels = selectedModels.filter(modelId => !inactiveModels.has(modelId));
      
      console.log('🔍 [handleSendMessage] Active models check', {
        selectedModels: selectedModels.length,
        inactiveModels: inactiveModels.size,
        activeModels: activeModels.length
      });
      
      // Ensure we have at least 1 active model
      if (activeModels.length === 0) {
        console.error('🔍 [handleSendMessage] ❌ Cannot send message: No active models available');
        isSendingMessageRef.current = false; // Reset ref on error
        return;
      }
      
      console.log('🔍 [handleSendMessage] 📤 Proceeding to add message to Firestore');

      // Use the count of active models for targetCount
      const activeTargetCount = activeModels.length;

      // Use override values if provided (for URL parameter auto-submission)
      // MUST be defined BEFORE modelDetails mapping uses it
      const effectivePromptSource = options?.promptSourceOverride ?? promptSource;
      const effectiveBaselineFromUrl = options?.baselineFromUrlOverride ?? baselineFromUrl;
      const effectiveSelfEvalEnabled = options?.selfEvalEnabled ?? selfEvalEnabled;
      const effectivePlusHumanEnabled = options?.plusHumanEnabled ?? plusHumanEnabled;
      const effectiveHumanLeaderboardEnabled = options?.humanLeaderboardEnabled ?? humanLeaderboardEnabled;
      const effectivePrioritize = options?.prioritizeOverride ?? prioritize;
      const effectiveTargetCount = options?.targetCountOverride ?? activeTargetCount;
      const effectiveReasoningEffort = options?.reasoningEffortOverride ?? reasoningEffort;

      // Gather full model details for sendConfig
      const modelDetails = activeModels.map(openrouterId => {
        const model = allModels.find(m => m.openrouterId === openrouterId);
        
        // Parse aaModelName for reasoning effort keywords
        // Extract "(high)" or "(low)" from AA model name (e.g., "GPT-5.1 (high)")
        let modelReasoningDefault: 'high' | 'low' | null = null;
        const aaModelName = allOpenRouterModels.find(m => m.id === openrouterId)?.name;
        if (aaModelName) {
          const match = aaModelName.match(/\((high|low)\)/i);
          if (match) {
            modelReasoningDefault = match[1].toLowerCase() as 'high' | 'low';
          }
        }
        
        // Determine effective reasoning effort for this model:
        // - If slider is "low" or "high", use slider value (override)
        // - If slider is "default" or "medium", use model's default from AA name if available
        // - Otherwise, use "default" (medium is treated as default)
        let effectiveModelReasoning: 'default' | 'low' | 'high';
        if (effectiveReasoningEffort === 'low' || effectiveReasoningEffort === 'high') {
          // Slider overrides model default
          effectiveModelReasoning = effectiveReasoningEffort;
        } else if (modelReasoningDefault) {
          // Slider is "default" or "medium", use model's extracted default
          effectiveModelReasoning = modelReasoningDefault;
        } else {
          // No model default, use "default" (medium is treated as default)
          effectiveModelReasoning = 'default';
        }
        
        return {
          openrouterId,
          name: model?.name || openrouterId,
          creator: model?.modelCreator || 'Unknown',
          reasoning: effectiveModelReasoning
        };
      });
      const effectiveWebEnabled = options?.webEnabledOverride ?? webEnabled;

      // Create sendConfig snapshot
      console.log('📋 [sendConfig] activeRubricId at time of send:', activeRubricId);
      const sendConfig: any = {
        prioritize: effectivePrioritize,
        ais: effectiveTargetCount,
        reasoning: effectiveReasoningEffort,
        webGrounding: effectiveWebEnabled,
        selfeval: effectiveSelfEvalEnabled,
        txt: txtEnabled,
        enterSends: enterSendsEnabled,
        selfGradeEnabled: selfgradeEnabled, // System setting at time of send
        promptSource: effectivePromptSource,
        baselineFromUrl: effectiveBaselineFromUrl,
        plusHumanEnabled: effectivePlusHumanEnabled,
        selectedModels: modelDetails,
        ...(activeRubricId && { activeRubricId }), // Custom review rubric if enabled
      };
      console.log('📋 [sendConfig] full sendConfig:', sendConfig);
      
      // Only include optional fields if they have values (Firestore rejects undefined)
      if (effectivePlusHumanEnabled) {
        sendConfig.humanLeaderboardEnabled = effectiveHumanLeaderboardEnabled;
      }
      // 🎯 PHASE 2: Include edit metadata if this is an edited prompt
      if (options?.isEditOf) {
        sendConfig.isEditOf = options.isEditOf;
        console.log('✏️ This is an edit of prompt:', options.isEditOf);
      }
      if (options?.humanDatasetDocId) {
        sendConfig.humanDatasetDocId = options.humanDatasetDocId;
      }
      if (options?.humanDatasetCardId) {
        sendConfig.humanDatasetCardId = options.humanDatasetCardId;
      }
      if (options?.humanDatasetSessionId) {
        sendConfig.humanDatasetSessionId = options.humanDatasetSessionId;
      }
      if (options?.humanDatasetSessionTitle) {
        sendConfig.humanDatasetSessionTitle = options.humanDatasetSessionTitle;
      }

      // Create chip-ID-based assignments for immutable color/model pairing
      // This is the canonical source of truth that eliminates string matching fragility
      const chipAssignments = await Promise.all(activeModels.map(async (openrouterId, chipId) => {
        const color = highlighting.HIGHLIGHTER_COLORS[chipId % highlighting.HIGHLIGHTER_COLORS.length].hex;
        const displayedModelID = await getDisplayedModelID(openrouterId);
        
        return {
          chipId,
          color,
          openrouterId,
          displayedModelID
        };
      }));
      
      console.log('🎨 Created chip assignments:', chipAssignments);
      
      // Add user message to Firestore with current slider values and toggle states
      // If a new conversation needs to be created, it will use these values
      // The Cloud Function will automatically process this and add an AI response
      console.log('🔍 Sending message with selfEvalEnabled:', effectiveSelfEvalEnabled, 'plusHumanEnabled:', effectivePlusHumanEnabled, 'plusHumanContent:', plusHumanContent);
      console.log('🔍 Using priority:', effectivePrioritize, 'targetCount:', effectiveTargetCount, 'reasoning:', effectiveReasoningEffort);
      const result = await addMessage(
        content,
        'user',
        effectiveTargetCount,
        effectiveReasoningEffort,
        effectivePrioritize,
        activeModels,
        effectiveWebEnabled,
        effectiveSelfEvalEnabled,
        txtEnabled,
        sendConfig,
        plusHumanContent,
        undefined, // batchRunID
        undefined, // hmaicbTimestamp
        modelColorAssignments, // LEGACY: Keep for backward compatibility during transition
        showModelNames, // Freeze display mode (creator vs model names) for this prompt
        chipAssignments // NEW: Immutable chip-ID based assignments
      );
      
      // Create debug info message immediately after user message
      if (result) {
        const { messageId: userMessageId, conversationId } = result;
        
        // 💰 IONOFF COST: Track this prompt for cost calculation
        // prePromptOrUsage will be set by the backend and read from Firestore later
        ionoffCostOnPromptSent(userMessageId);
        invalidateBalanceCache();
        window.setTimeout(() => void loadPersonalBalance(), 3000);

        // CRITICAL: Update the URL after first message is sent
        // This ensures the URL reflects the current conversation for refresh persistence
        // Use replaceState instead of navigate to avoid re-mounting ChatLayout (which resets state)
        if (conversationId && window.location.pathname === '/') {
          urlLoadedRef.current = conversationId; // Mark as loaded to prevent re-triggering
          window.history.replaceState({}, '', `/c/${conversationId}`);
          console.log('🔗 Updated URL to conversation:', conversationId);
        }
        
        // 🎯 COACHING: Generate coaching pointer in parallel with main prompt processing
        // Detect device type for coaching context
        const deviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';
        generateCoaching(conversationId, userMessageId, content, deviceType)
          .then(coachingResult => {
            // 💰 Track coaching cost for ionoff cost calculation (to subtract from delta)
            if (coachingResult?.costUSD) {
              onCoachingCostReceived(userMessageId, coachingResult.costUSD);
            }
          })
          .catch(err => console.warn('Coaching generation failed (non-blocking):', err));
        
        const timestamp = new Date().toISOString();
        const modelList = modelDetails.map((m, i) => `${i + 1}. ${m.name} (${m.creator})`).join('\n');
        const promptSourceLabel = effectivePromptSource === 'dataset'
          ? '📚 Dataset (human-meets-ai)'
          : effectivePromptSource === 'url'
            ? '🔗 URL'
            : '⌨️ Manual Entry';
        const datasetDetails = options?.humanDatasetDocId
          ? `\n• **Human Dataset Doc:** ${options.humanDatasetDocId}` +
            (options.humanDatasetCardId ? `\n• **Human Dataset Card:** ${options.humanDatasetCardId}` : '') +
            (options.humanDatasetSessionTitle ? `\n• **Human Dataset Session:** ${options.humanDatasetSessionTitle}` : '')
          : '';
        
        const debugContent = `🔧 **Debug Info**

**Message ID:** \`${userMessageId}\`
**Conversation ID:** \`${conversationId}\`
**Timestamp:** ${timestamp}

**Settings:**
• **Prioritize:** ${prioritize}
• **AIs:** ${activeTargetCount}
• **Reasoning:** ${reasoningEffort}
• **Web Grounding:** ${webEnabled ? '✓ Enabled' : '✗ Disabled'}
• **Self-Eval:** ${effectiveSelfEvalEnabled ? '✓ Enabled' : '✗ Disabled'}
• **TxT:** ${txtEnabled ? '✓ Enabled' : '✗ Disabled'}
• **+Human:** ${plusHumanEnabled ? '✓ Enabled' : '✗ Disabled'}
${plusHumanEnabled ? `• **Leaderboard:** ${humanLeaderboardEnabled ? '✓ Enabled' : '✗ Disabled'}` : ''}
• **Custom Review:** ${activeRubricId ? `✓ ${activeRubric?.name || activeRubricId}` : '✗ Disabled'}
• **Send Option:** ${enterSendsEnabled ? 'Enter sends' : 'Shift+Enter sends'}
• **Prompt Source:** ${promptSourceLabel}
• **Baseline:** ${effectiveBaselineFromUrl ? '🔗 Provided via URL' : '✗ Not Provided'}${datasetDetails}

**Models:**
${modelList}`;

        await ConversationService.addMessage(conversationId, {
          role: 'assistant',
          content: debugContent,
          modelUsed: 'ionoff-debug-info',
          isDebugInfo: true,
          debugIds: {
            messageId: userMessageId,
            conversationId: conversationId,
            userPromptMessageId: userMessageId,
            timestamp: timestamp
          },
          sendConfig: sendConfig
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      // Always reset the flag to allow future messages
      isSendingMessageRef.current = false;
      // 🎯 PHASE 2: Clear edit state after sending
      if (options?.isEditOf) {
        handleClearEdit();
      }
    }
  }, [user, addMessage, targetCount, reasoningEffort, prioritize, selectedModels, inactiveModels, webEnabled, selfEvalEnabled, txtEnabled, enterSendsEnabled, allModels, promptSource, baselineFromUrl, plusHumanEnabled, humanLeaderboardEnabled, handleClearEdit, navigate, selfgradeEnabled, activeRubricId, activeRubric, generateCoaching, onCoachingCostReceived, loadPersonalBalance]);

  // 🔐 Deferred Prompt: Send pending prompt when component is ready
  // This useEffect watches for when user is authenticated AND models are loaded
  // Works for both Stripe payment flow and magic link sign-in flow
  useEffect(() => {
    if (deferredPendingPrompt && user && selectedModels.length > 0) {
      console.log(`🔐 [${deferredPendingPrompt.source}] Component ready, sending pending prompt now`, {
        user: user.uid,
        selectedModelsCount: selectedModels.length,
        promptPreview: deferredPendingPrompt.prompt.substring(0, 50)
      });
      
      // Clear the pending prompt first to prevent re-sends
      const promptToSend = deferredPendingPrompt;
      setDeferredPendingPrompt(null);
      
      // Send the message
      handleSendMessage(promptToSend.prompt, promptToSend.plusHuman);
    }
  }, [deferredPendingPrompt, user, selectedModels.length, handleSendMessage]);

  // 🎯 PHASE 2: Handle prompt editing
  const handleEditPrompt = useCallback((promptId: string, promptContent: string) => {
    console.log('✏️ Editing prompt:', { promptId, contentPreview: promptContent.substring(0, 50) });
    setEditingPromptId(promptId);
    setEditingPromptContent(promptContent);
    // Expand top section so user can see the input
    setIsTopSectionCollapsed(false);
  }, []);

  // Admin: Load N most recent ACTIVE conversations from all users
  const loadAdminConversations = useCallback(async (count: number) => {
    console.log(`🔧 Admin load called with count: ${count}, user: ${user?.uid || 'none'}`);
    
    if (!user) {
      console.error('❌ Cannot load admin conversations: User not authenticated');
      return;
    }
    
    if (count <= 0) {
      console.error('❌ Cannot load admin conversations: Invalid count');
      return;
    }
    
    setIsLoadingAdminConversations(true);
    try {
      console.log(`🔧 Loading ${count} most recent ACTIVE conversations from all users...`);
      
      // Query Firestore for N most recent ACTIVE conversations across all users
      // Note: This requires a Firestore index on (active, lastUpdatedAt)
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('active', '==', true),
        orderBy('lastUpdatedAt', 'desc'),
        limit(count)
      );
      
      const snapshot = await getDocs(conversationsQuery);
      const fetchedConversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        lastUpdatedAt: doc.data().lastUpdatedAt?.toDate() || new Date()
      })) as any[];
      
      console.log(`✅ Fetched ${fetchedConversations.length} active conversations`, {
        conversationIds: fetchedConversations.map((c: any) => c.id)
      });
      
      // Add to conversation list (hook handles deduplication and sorting)
      addExternalConversations(fetchedConversations as any);
      
      // Reset input
      setShowAdminInput(false);
      setAdminConversationCount('');
      
    } catch (error: any) {
      // If the error is about a missing index, provide helpful message
      if (error?.code === 'failed-precondition' && error?.message?.includes('index')) {
        console.error('❌ Failed to load admin conversations: Firestore index required');
        console.error('   Please create an index on: conversations (active, lastUpdatedAt)');
        console.error('   Error details:', error.message);
      } else {
        console.error('❌ Failed to load admin conversations:', error);
      }
    } finally {
      setIsLoadingAdminConversations(false);
    }
  }, [user, addExternalConversations]);

  // BACKWARD COMPATIBILITY: Redirect legacy ?conversationId=xxx to /c/xxx
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const legacyConversationId = urlParams.get('conversationId');
    
    if (legacyConversationId) {
      console.log('🔄 Redirecting legacy URL to new path-based format:', legacyConversationId);
      navigate(`/c/${legacyConversationId}`, { replace: true });
    }
  }, [navigate]);

  // CRITICAL: Load conversation from URL path (e.g., /c/:conversationId)
  // This effect runs when urlConversationId from useParams changes
  const urlLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    console.log('🔗 URL loading effect running:', {
      urlConversationId,
      urlLoadedRef: urlLoadedRef.current,
      hasUser: !!user,
      currentConversationId
    });
    
    // If there's no conversationId in URL path, nothing to do
    if (!urlConversationId) {
      console.log('⏭️ No conversationId in URL path');
      return;
    }
    
    // If already loaded this exact URL, don't load again
    if (urlLoadedRef.current === urlConversationId) {
      console.log('⏭️ URL already loaded, skipping');
      return;
    }
    
    // Wait for user to be authenticated before loading conversation
    if (!user) {
      console.log('⏳ Waiting for user authentication before loading URL conversation');
      return;
    }
    
    // User is authenticated and we have a conversationId - load it!
    console.log('🔗 Loading conversation from URL path:', urlConversationId);
    urlLoadedRef.current = urlConversationId; // Mark as loaded BEFORE async operation
      
    // CRITICAL: Load parameters BEFORE switching to avoid showing stale defaults
    // This ensures priority slider and leaderboards are correct on page refresh
    (async () => {
      try {
        const params = await ConversationService.getConversationParameters(urlConversationId);
        if (params) {
          if (params.prioritize) {
            const safePrioritize = params.prioritize === 'cost' ? 'balance' : params.prioritize;
            setPrioritize(safePrioritize as 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom');
            console.log('🔗 URL load: Set priority to', safePrioritize);
          }
          if (params.targetCount !== undefined) {
            setTargetCount(params.targetCount);
          }
          if (params.reasoningEffort) {
            setReasoningEffort(params.reasoningEffort);
          }
        }
      } catch (error) {
        console.error('Failed to load conversation parameters from URL:', error);
      }
      
      // Now switch to the conversation with parameters already set
      switchToConversation(urlConversationId);
    })();
  }, [user, switchToConversation, urlConversationId]); // Depends on urlConversationId from useParams

  // Development-only: Auto-submit message from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('testMode') === 'true';
    const isDev = process.env.NODE_ENV === 'development';
    const hasPromptParam = urlParams.get('prompt') !== null;
    
    console.log('🔍 [AUTO-SUBMIT EFFECT] Running effect', {
      hasPromptParam,
      user: !!user,
      modelLoading,
      conversationLoading,
      messagesLength: messages.length,
      currentConversationId: currentConversationId?.substring(0, 20),
      refBlocked: datasetAutoSubmitRef.current
    });

    // Allow prompt/humanResponse params in dev mode or with testMode flag
    if (!hasPromptParam && !ENABLE_AUTO_DATASET_SUBMISSION) {
      if (autoHumanDatasetEnabled) {
        setAutoHumanDatasetEnabled(false);
      }
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: no prompt param and auto-dataset disabled');
      return;
    }

    // For non-prompt params, enforce dev/testMode requirement
    if (!hasPromptParam && !isDev && !testModeParam) {
      setAutoHumanDatasetEnabled(false);
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: not in dev mode and no test mode');
      return;
    }
    
    const testMessage = urlParams.get('testMessage');
    const baseline = urlParams.get('baseline');
    const useDatasetParam = urlParams.get('useHumanDataset');
    const shouldUseDataset = useDatasetParam === 'true';
    setAutoHumanDatasetEnabled(shouldUseDataset);
    
    // Check for direct prompt/humanResponse URL parameters (for Playwright batch testing)
    const promptParam = urlParams.get('prompt');
    
    // CRITICAL: Apply URL parameters FIRST (before checking selectedModels)
    // This ensures fetchModels runs when priority/targetCount change from URL params
    // Apply even if modelLoading is true, as applying params will trigger fetchModels
    if (promptParam && user) {
      const testTargetCount = urlParams.get('targetCount');
      const testReasoningEffort = urlParams.get('reasoningEffort');
      const testPrioritize = urlParams.get('prioritize');
      const testWebEnabled = urlParams.get('webEnabled');
      const testSelfEvalEnabled = urlParams.get('selfEvalEnabled');
      const testTxtEnabled = urlParams.get('txtEnabled');
      const testPlusHumanEnabled = urlParams.get('plusHumanEnabled');
      
      // Apply URL parameters to trigger fetchModels if needed
      let paramsChanged = false;
      let needsDirectFetch = false;
      
      if (testTargetCount && parseInt(testTargetCount, 10) !== targetCount) {
        setTargetCount(parseInt(testTargetCount, 10));
        paramsChanged = true;
      }
      if (testReasoningEffort && testReasoningEffort !== reasoningEffort) {
        setReasoningEffort(testReasoningEffort as any);
        paramsChanged = true;
      }
      if (testPrioritize) {
        // CRITICAL: If URL priority matches current state, we need to call fetchModels directly
        // since setPrioritize won't trigger a state change
        if (testPrioritize === prioritize) {
          console.log('🔍 [AUTO-SUBMIT EFFECT] URL priority matches current state - will call fetchModels directly');
          needsDirectFetch = true;
          paramsChanged = true;
        } else {
          setPrioritize(testPrioritize as any);
          paramsChanged = true;
        }
      }
      
      // Mark that we just applied URL parameters
      if (paramsChanged) {
        urlParamsAppliedRef.current = true;
        console.log('🔍 [AUTO-SUBMIT EFFECT] URL parameters applied, fetchModels should run');
        
        // If priority matched, call fetchModels directly since state won't change
        if (needsDirectFetch) {
          console.log('🔍 [AUTO-SUBMIT EFFECT] Calling fetchModels directly');
          fetchModels().then(() => {
            console.log('🔍 [AUTO-SUBMIT EFFECT] fetchModels completed, models should be ready');
          });
        }
      }
      if (testWebEnabled !== null && (testWebEnabled === 'true') !== webEnabled) {
        setWebEnabled(testWebEnabled === 'true');
      }
      if (testSelfEvalEnabled !== null && (testSelfEvalEnabled === 'true') !== selfEvalEnabled) {
        setSelfEvalEnabled(testSelfEvalEnabled === 'true');
      }
      if (testTxtEnabled !== null && (testTxtEnabled === 'true') !== txtEnabled) {
        setTxtEnabled(testTxtEnabled === 'true');
      }
      if (testPlusHumanEnabled !== null && (testPlusHumanEnabled === 'true') !== plusHumanEnabled) {
        setPlusHumanEnabled(testPlusHumanEnabled === 'true');
      }
    }
    
    // Wait for auth and models to load, and ensure we haven't already submitted
    // CRITICAL: If we just applied URL parameters, fetchModels will run asynchronously
    // We need to wait for it to complete before submitting
    // The effect will re-run when selectedModels changes (it's in the dependency array)
    // BUT: If we just applied URL parameters in this effect run, we should allow it to continue
    // and let the next effect run (after fetchModels completes) handle the submission
    const justAppliedUrlParams = promptParam && user && !modelLoading;
    
    if (!user || modelLoading || conversationLoading) {
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: waiting for prerequisites', {
        user: !!user,
        modelLoading,
        conversationLoading,
        selectedModelsCount: selectedModels.length
      });
      return;
    }
    
    // Only require selectedModels if we haven't just applied URL parameters
    // (If we just applied them, fetchModels is running and will populate selectedModels)
    // Reset the ref once models are ready
    if (urlParamsAppliedRef.current && selectedModels.length > 0) {
      urlParamsAppliedRef.current = false;
      console.log('🔍 [AUTO-SUBMIT EFFECT] Models ready after URL params applied');
    }
    
    if (!urlParamsAppliedRef.current && selectedModels.length === 0) {
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: waiting for models', {
        selectedModelsCount: selectedModels.length,
        urlParamsApplied: urlParamsAppliedRef.current
      });
      return;
    }
    
    // If we just applied URL params but models aren't ready yet, wait for next effect run
    if (urlParamsAppliedRef.current && selectedModels.length === 0) {
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: URL params applied, waiting for fetchModels to populate selectedModels');
      return;
    }
    
    console.log('🔍 [AUTO-SUBMIT EFFECT] Prompt param check', {
      promptParam: !!promptParam,
      currentConversationId: currentConversationId?.substring(0, 20),
      messagesLength: messages.length
    });
    
    // If prompt parameter is present, ensure we have a fresh conversation
    // This is critical for batch testing where each page.goto() should create a new conversation
    if (promptParam) {
      // Extract batchRunID, hmaicbTimestamp, and published from URL params (for batch test tracking)
      const batchRunIDParam = urlParams.get('batchRunID');
      const hmaicbTimestampParam = urlParams.get('hmaicbTimestamp');
      const publishedParam = urlParams.get('published') === 'true';
      
      // If we have an existing conversation with messages, start a new one
      if (currentConversationId && messages.length > 0) {
        console.log('🔍 [AUTO-SUBMIT EFFECT] 🔄 Existing conversation with messages - starting new conversation');
        // Reset the auto-submit ref so we can submit to the new conversation
        datasetAutoSubmitRef.current = false;
        // Start a new conversation to ensure we don't reuse the previous one
        // Use async IIFE since useEffect can't be async
        (async () => {
          try {
            await startNewConversation(
              undefined, 
              undefined, 
              undefined, 
              undefined, 
              batchRunIDParam || undefined,
              hmaicbTimestampParam || undefined,
              publishedParam || undefined
            );
            // Wait a bit for the new conversation to be set up
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (error) {
            console.error('Failed to start new conversation for batch test:', error);
          }
        })();
        return; // Return early - will retry on next effect run after new conversation is created
      }
      
      // If no conversation exists yet, ensure we have one before submitting
      // This is important for the first conversation in a batch test
      if (!currentConversationId) {
        console.log('🔍 [AUTO-SUBMIT EFFECT] 🔄 No conversation - creating one');
        // Reset the auto-submit ref
        datasetAutoSubmitRef.current = false;
        // Start a new conversation first and WAIT for the state to actually update
        (async () => {
          try {
            const newConvId = await startNewConversation(
              undefined, 
              undefined, 
              undefined, 
              undefined, 
              batchRunIDParam || undefined,
              hmaicbTimestampParam || undefined,
              publishedParam || undefined
            );
            console.log(`🔍 [AUTO-SUBMIT EFFECT] Conversation created: ${newConvId.substring(0, 20)}...`);
            
            // CRITICAL: Poll until currentConversationId state is actually set
            // Don't rely on arbitrary timeouts
            let pollAttempts = 0;
            const maxPollAttempts = 20; // 10 seconds max (20 * 500ms)
            
            while (pollAttempts < maxPollAttempts) {
              // Check if React state has caught up
              // Use a small delay to allow React to process the state update
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // The effect will re-run when currentConversationId changes, so we can break here
              // The next run will see currentConversationId and proceed to submission
              console.log(`🔍 [AUTO-SUBMIT EFFECT] Polling for state update (attempt ${pollAttempts + 1}/${maxPollAttempts})`);
              pollAttempts++;
              
              // Break early - the effect will re-run due to currentConversationId dependency
              break;
            }
          } catch (error) {
            console.error('Failed to start new conversation for batch test:', error);
          }
        })();
        return; // Return early - will retry on next effect run after conversation is created
      }
      
      // If we have a conversation ID but no messages yet, proceed to submission
      // This is the correct state for auto-submission (conversation exists, no messages yet)
      // The check below (messages.length > 0) will prevent duplicate submissions
      console.log('🔍 [AUTO-SUBMIT EFFECT] ✅ Conversation exists, proceeding to submission checks', {
        conversationId: currentConversationId?.substring(0, 20),
        selectedModelsReady: selectedModels.length > 0,
        messagesCount: messages.length
      });
    }
    
    // Only submit if there are no messages yet (to avoid duplicate submissions)
    if (messages.length > 0) {
      console.log('🔍 [AUTO-SUBMIT EFFECT] Early return: messages already exist');
      return;
    }
    
    // Get prompt/humanResponse for auto-submission check
    const humanResponseParam = urlParams.get('humanResponse');
    
    console.log('🔍 [AUTO-SUBMIT EFFECT] Checking ref block', {
      refBlocked: datasetAutoSubmitRef.current,
      promptParam: !!promptParam
    });
    
    // Check if we should auto-submit BEFORE applying test parameters
    // This prevents the effect from re-running due to state changes from applyTestParameters
    if (promptParam) {
      if (datasetAutoSubmitRef.current) {
        console.log('🔍 [AUTO-SUBMIT EFFECT] ❌ BLOCKED by ref - already submitted or in progress');
        return;
      }
      datasetAutoSubmitRef.current = true;
      console.log('🔍 [AUTO-SUBMIT EFFECT] ✅ Ref set to true - proceeding with submission');
      
      console.log('🤖 Auto-submitting from URL parameters');
      console.log('   Prompt:', promptParam.substring(0, 60) + '...');
      if (humanResponseParam) {
        console.log('   Human response:', humanResponseParam.substring(0, 60) + '...');
      }
      
      // Get URL parameter values directly (don't set state yet to avoid re-triggering effect)
      const testTargetCount = urlParams.get('targetCount');
      const testReasoningEffort = urlParams.get('reasoningEffort');
      const testPrioritize = urlParams.get('prioritize');
      const testWebEnabled = urlParams.get('webEnabled');
      const testSelfEvalEnabled = urlParams.get('selfEvalEnabled');
      const testTxtEnabled = urlParams.get('txtEnabled');
      const testPlusHumanEnabled = urlParams.get('plusHumanEnabled');
      
      // Prepare override values for submission
      const urlPrioritize = testPrioritize as 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom' | null;
      const urlTargetCount = testTargetCount;
      const urlReasoningEffort = testReasoningEffort as 'default' | 'low' | 'medium' | 'high' | null;
      const urlWebEnabled = testWebEnabled;
      const urlSelfEvalEnabled = testSelfEvalEnabled;
      
      // Note: Priority matching case is now handled earlier when applying URL parameters
      
      // Extract hmaicbTimestamp from URL params
      const hmaicbTimestampParam = urlParams.get('hmaicbTimestamp');
      
      // Submit IMMEDIATELY with URL parameter values passed directly as overrides
      // This happens BEFORE any state changes that would re-trigger the effect
      console.log('🔍 [AUTO-SUBMIT EFFECT] 📤 Calling handleSendMessage NOW');
      handleSendMessage(promptParam, humanResponseParam || undefined, {
        promptSourceOverride: 'url',
        plusHumanEnabled: !!humanResponseParam,
        selfEvalEnabled: urlSelfEvalEnabled === 'true' || (urlSelfEvalEnabled === null && selfEvalEnabled),
        humanLeaderboardEnabled,
        hmaicbTimestamp: hmaicbTimestampParam || undefined,
        prioritizeOverride: urlPrioritize || undefined,
        targetCountOverride: urlTargetCount ? parseInt(urlTargetCount, 10) : undefined,
        reasoningEffortOverride: urlReasoningEffort || undefined,
        webEnabledOverride: urlWebEnabled !== null ? urlWebEnabled === 'true' : undefined,
      }).catch(console.error);
      
      // NOW apply state changes for UI consistency (after submission is initiated)
      // These will trigger the effect to re-run, but the ref will block it
      if (testTargetCount) setTargetCount(parseInt(testTargetCount, 10));
      if (testReasoningEffort) setReasoningEffort(testReasoningEffort as any);
      if (testPrioritize) setPrioritize(testPrioritize as any);
      if (testWebEnabled !== null) setWebEnabled(testWebEnabled === 'true');
      if (testSelfEvalEnabled !== null) setSelfEvalEnabled(testSelfEvalEnabled === 'true');
      if (testTxtEnabled !== null) setTxtEnabled(testTxtEnabled === 'true');
      if (testPlusHumanEnabled !== null) setPlusHumanEnabled(testPlusHumanEnabled === 'true');
      
      // Clean up URL and reset flags
      setTimeout(() => {
        setPromptSource('manual');
        setBaselineFromUrl(false);
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        datasetAutoSubmitRef.current = false;
      }, 500);
      
      return;
    }
    
    if (shouldUseDataset && !testMessage) {
      if (datasetAutoSubmitRef.current) return;
      datasetAutoSubmitRef.current = true;
      (async () => {
        try {
          const pair = await fetchRandomFirstSessionPair();
          if (!pair) {
            console.warn('Failed to fetch dataset prompt for test run.');
            return;
          }
          console.log('🤖 Auto-submitting dataset pair from human-meets-ai collection:', pair.docId);
          await handleSendMessage(pair.prompt, pair.response, {
            promptSourceOverride: 'dataset',
            plusHumanEnabled: true,
            selfEvalEnabled: true,
            humanLeaderboardEnabled,
            humanDatasetDocId: pair.docId,
            humanDatasetCardId: pair.cardId,
            humanDatasetSessionId: pair.sessionId,
            humanDatasetSessionTitle: pair.sessionTitle,
          });
        } catch (error) {
          console.error('Failed to auto-submit dataset pair:', error);
        } finally {
          setTimeout(() => {
            setPromptSource('manual');
            setBaselineFromUrl(false);
          }, 500);
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
          datasetAutoSubmitRef.current = false;
        }
      })();
      return;
    }
    
    if (!testMessage) return;
    
    // Set prompt source to 'url' since testMessage is provided
    setPromptSource('url');
    
    // Set baseline flag if baseline parameter is provided
    if (baseline) {
      setBaselineFromUrl(true);
    }
    
    const testNewConversation = urlParams.get('newConversation') === 'true';
    
    // Note: handleNewConversation is defined later, so we'll handle new conversation
    // by checking if we need to start fresh
    if (testNewConversation && currentConversationId) {
      // Delay to allow handleNewConversation to be defined
      setTimeout(async () => {
        try {
          await startNewConversation(targetCount, reasoningEffort, prioritize, selectedModels);
          // Wait a bit then submit
          setTimeout(() => {
            console.log('🤖 Auto-submitting test message from URL:', testMessage);
            handleSendMessage(testMessage).catch(console.error);
            
            // Reset flags for subsequent manual messages
            setTimeout(() => {
              setPromptSource('manual');
              setBaselineFromUrl(false);
            }, 500);
            
            // Clean up URL parameter after submission
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }, 1000);
        } catch (error) {
          console.error('Failed to start new conversation:', error);
        }
      }, 100);
    } else {
      // Auto-submit the message
      console.log('🤖 Auto-submitting test message from URL:', testMessage);
      handleSendMessage(testMessage).catch(console.error);
      
      // Reset flags for subsequent manual messages
      setTimeout(() => {
        setPromptSource('manual');
        setBaselineFromUrl(false);
      }, 500);
      
      // Clean up URL parameter after submission
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [user, modelLoading, conversationLoading, messages.length, handleSendMessage, currentConversationId, reasoningEffort, prioritize, selectedModels, startNewConversation, targetCount, humanLeaderboardEnabled, autoHumanDatasetEnabled, fetchModels]);

  const handleNewConversation = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      // Reset prompt settings for new prompt (per-prompt defaults)
      setToolsExpanded(false);
      setReasoningEffort('default');
      setWebEnabled(true);
      setEnterSendsEnabled(!isMobileOrTablet());
      
      // Reset windowshades
      setIsTopSectionCollapsed(true);
      setIsPeerReviewSettingsExpanded(false);
      
      // Start a new conversation with current peer review settings (persisted at userId level)
      await startNewConversation(targetCount, reasoningEffort, prioritize, selectedModels);
      
      // Navigate to root URL (shows landing page until first message is sent)
      urlLoadedRef.current = null; // Clear the loaded ref
      navigate('/');
      
      // Open sidebar if user has conversations (they now have at least one in recents)
      if (conversations.length > 0) {
        setIsSidebarOpen(true);
      }
      
      console.log('🔗 Navigated to root for new conversation');
    } catch (error) {
      console.error('Failed to start new conversation:', error);
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    // Prevent multiple simultaneous conversation switches
    if (isSwitchingConversationRef.current) {
      console.log('Conversation switch already in progress, ignoring');
      return;
    }
    
    // Set flag to prevent conversation listener from triggering circular update
    isSwitchingConversationRef.current = true;
    
    // Reset windowshades on conversation switch
    setIsTopSectionCollapsed(true);
    setIsPeerReviewSettingsExpanded(false);
    
    // Reset prompt settings for new prompt (per-prompt defaults)
    setToolsExpanded(false);
    setReasoningEffort('default');
    setWebEnabled(true);
    setEnterSendsEnabled(!isMobileOrTablet());
    
    // NOTE: Peer review settings (prioritize, targetCount, peer review level) 
    // are NOT loaded from conversation - they persist at userId level
    
    // Load only model selection from conversation
    try {
      const params = await ConversationService.getConversationParameters(conversationId);
      if (params && params.selectedModels && params.selectedModels.length > 0) {
        setSelectedModels(params.selectedModels);
        
        // If conversation was in custom mode, update customModels too
        if (params.prioritize === 'custom') {
          setCustomModels(params.selectedModels);
          console.log('📊 Loaded custom mode selection:', params.selectedModels);
        }
        
        setInactiveModels(new Set());
      } else {
        setSelectedModels([]);
        setInactiveModels(new Set());
      }
    } catch (error) {
      console.error('Failed to load conversation models:', error);
    }
    
    // Small delay before switching to allow current listeners to settle
    // This prevents Firestore internal assertion errors during rapid listener changes
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Switch to the conversation AFTER parameters are set and delay
    switchToConversation(conversationId);
    
    // CRITICAL: Update URL to reflect the current conversation (path-based routing)
    // Mark this URL as already loaded to prevent the URL effect from re-running
    urlLoadedRef.current = conversationId;
    navigate(`/c/${conversationId}`);
    console.log('🔗 Navigated to conversation:', conversationId);
    
    // Reset flag after delay to ensure all Firestore updates are processed
    // Using 2 second delay to allow time for conversation switch and listener setup
    setTimeout(() => {
      isSwitchingConversationRef.current = false;
      console.log('Conversation switch complete - re-enabling listener');
    }, 2000);
  };

  const handlePrioritizeChange = async (value: number[]) => {
    if (requiresPaymentGate()) return;
    // Dynamically build options array based on whether custom has been activated
    const prioritizeOptions: Array<'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom'> = 
      hasCustomized 
        ? ['free', 'speed', 'balance', 'value', 'intelligence', 'custom']
        : ['free', 'speed', 'balance', 'value', 'intelligence'];
    
    const newPrioritize = prioritizeOptions[value[0]];
    console.log('🎯 Priority changed:', newPrioritize);
    
    // Mark control as interacted (hides help icon for this user)
    markInteracted('prioritize');
    
    // If switching TO custom mode and we have no custom models, initialize with current selection
    if (newPrioritize === 'custom' && customModels.length === 0) {
      console.log('  → Initializing custom models with current selection:', selectedModels);
      setCustomModels(selectedModels);
      
      // Save to user profile
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          customModels: selectedModels
        }, { merge: true });
      }
    }
    
    setPrioritize(newPrioritize);
    
    // Clear all manual model inactivations when prioritize slider changes
    setInactiveModels(new Set());
    setMarginToggledModels(new Set()); // Also clear gray backgrounds
    console.log('🔄 Cleared manual model inactivations due to prioritize change');
    
    if (!user) {
      console.warn('⚠️ User not authenticated yet');
      return;
    }

    try {
      const convId = currentConversationId;

      // Only update existing conversation, don't create a new one
      if (!convId) {
        return; // prioritize is already in state, will be used on conversation creation
      }

      // Update prioritize and clear inactive models in existing conversation
      await updateDoc(doc(db, 'conversations', convId), {
        'modelParameters.prioritize': newPrioritize,
        'modelParameters.inactiveModels': []
      });
      console.log(`✅ Updated prioritize to ${newPrioritize} and cleared inactive models`);
    } catch (error) {
      console.error('Failed to update prioritize:', error);
    }
  };

  const handleToggleModel = async (modelId: string) => {
    try {
      // Set flag to prevent model fetch during toggle
      isTogglingRef.current = true;

      const newInactiveModels = new Set(inactiveModels);
      const newMarginToggledModels = new Set(marginToggledModels);
      
      if (newInactiveModels.has(modelId)) {
        // Reactivate the model
        newInactiveModels.delete(modelId);
        newMarginToggledModels.delete(modelId); // Remove gray background
      } else {
        // Deactivate the model (only if there's at least one other active model)
        const activeCount = selectedModels.filter(id => !newInactiveModels.has(id)).length;
        if (activeCount <= 1) {
          console.warn('Cannot deactivate last active model');
          isTogglingRef.current = false;
          return;
        }
        newInactiveModels.add(modelId);
        newMarginToggledModels.add(modelId); // Add gray background
      }

      setInactiveModels(newInactiveModels);
      setMarginToggledModels(newMarginToggledModels);

      // Save inactive models to Firestore if conversation exists
      if (currentConversationId) {
        const conversationRef = doc(db, 'conversations', currentConversationId);
        await updateDoc(conversationRef, {
          'modelParameters.inactiveModels': Array.from(newInactiveModels),
        });
      }

      console.log('Model toggled:', modelId, 'inactive:', newInactiveModels.has(modelId), 'activeCount:', selectedModels.length - newInactiveModels.size);
    } catch (error) {
      console.error('Error toggling model:', error);
    } finally {
      // Reset flag after toggle completes
      isTogglingRef.current = false;
    }
  };

  const handleToggleMargin = (modelId: string) => {
    setMarginToggledModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  const handleToggleChipView = () => {
    setShowModelNames(prev => !prev);
  };

  // Fetch all OpenRouter models from cached Firestore collection
  const fetchAllOpenRouterModels = async () => {
    setIsLoadingModels(true);
    try {
      console.log('🔍 Fetching all OpenRouter models from Firestore cache...');
      
      // Fetch all cached OpenRouter models (includes balanced scores)
      const modelsRef = collection(db, 'system', 'allOpenRouterModels', 'models');
      const modelsSnapshot = await getDocs(modelsRef);
      
      // Fetch AA model names from modelLists (more reliable than openrouterToAa)
      // This document contains all priority lists with comprehensive AA name mappings
      const modelListsDoc = await getDoc(doc(db, 'system', 'modelLists'));
      const aaNameMap = new Map<string, string>();
      
      if (modelListsDoc.exists()) {
        const allData = modelListsDoc.data();
        // Traverse ALL lists in modelLists document (not just a fixed set)
        // Schema: { free: {models: [...]}, top: {models: [...]}, speedy: {models: [...]}, etc. }
        Object.keys(allData).forEach(listKey => {
          const listData = allData[listKey];
          if (listData && typeof listData === 'object' && Array.isArray(listData.models)) {
            listData.models.forEach((model: any) => {
              if (model.openrouterId && model.name) {
                // Use lowercase for case-insensitive matching
                const key = model.openrouterId.toLowerCase();
                // Don't overwrite if already exists (first occurrence wins)
                if (!aaNameMap.has(key)) {
                  aaNameMap.set(key, model.name);
                }
              }
            });
          }
        });
      }
      
      const allModels: Array<{id: string; name: string; combinedRank: number | null; defaultReasoningEffort: string | null}> = [];
      
      modelsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.id) {
          allModels.push({
            id: data.id,
            name: aaNameMap.get(data.id.toLowerCase()) || data.id,
            combinedRank: data.combinedRank || null,
            defaultReasoningEffort: data.defaultReasoningEffort || null
          });
        }
      });
      
      console.log(`✅ Loaded ${allModels.length} OpenRouter models from cache (with ${aaNameMap.size} AA name mappings from modelLists)`);
      setAllOpenRouterModels(allModels);
    } catch (error) {
      console.error('❌ Error fetching OpenRouter models:', error);
    } finally {
      setIsLoadingModels(false);
    }
  };

  // Fetch all OpenRouter models on mount (needed for leaderboard filtering)
  useEffect(() => {
    fetchAllOpenRouterModels();
  }, []);

  // Calculate available models count (total - selected)
  const availableModelsCount = useMemo(() => {
    const selectedSet = new Set(selectedModels);
    return allOpenRouterModels.filter(model => !selectedSet.has(model.id)).length;
  }, [allOpenRouterModels, selectedModels]);

  // Filter and sort models based on search query
  // Exclude models that are already selected
  const filteredModels = useMemo(() => {
    // First, exclude already selected models
    const selectedSet = new Set(selectedModels);
    const availableModels = allOpenRouterModels.filter(
      model => !selectedSet.has(model.id)
    );
    
    let filtered: Array<{id: string; name: string; combinedRank: number | null; defaultReasoningEffort: string | null}>;
    
    if (!modelSearchQuery.trim()) {
      filtered = availableModels.slice(0, 50); // Show first 50 when no search
    } else {
      const query = modelSearchQuery.toLowerCase().trim();
      filtered = availableModels.filter(model => 
        model.id.toLowerCase().includes(query) ||
        (model.name && model.name.toLowerCase().includes(query))
      ).slice(0, 100); // Limit to 100 results
    }
    
    // Sort by combined rank (lowest = best), then by id for those without ranks
    const sorted = filtered.sort((a, b) => {
      // Models with combined ranks come first
      if (a.combinedRank !== null && b.combinedRank !== null) {
        return a.combinedRank - b.combinedRank; // Lower rank = better
      }
      if (a.combinedRank !== null) return -1; // a has rank, b doesn't - a comes first
      if (b.combinedRank !== null) return 1;  // b has rank, a doesn't - b comes first
      // Both don't have ranks - sort by id
      return a.id.localeCompare(b.id);
    });
    
    return sorted;
  }, [allOpenRouterModels, modelSearchQuery, selectedModels]);

  // Handle adding a model - switches to custom mode
  const handleAddModel = async (modelId: string) => {
    console.log('➕ Adding model:', modelId);
    
    // Prevent adding if already selected
    if (selectedModels.includes(modelId)) {
      console.warn('Model already selected');
      return;
    }

    // Prevent adding if already at max (10 models)
    if (selectedModels.length >= 10) {
      console.warn('Cannot add model: Maximum of 10 models allowed');
      return;
    }

    // Mark that user has customized (enables custom slider position)
    setHasCustomized(true);

    // Create new custom models list with added model
    const newCustomModels = [...selectedModels, modelId];
    setCustomModels(newCustomModels);
    console.log('  → Updated custom models:', newCustomModels);

    // Switch to custom mode
    setPrioritize('custom');
    console.log('  → Switched to custom mode');

    // Increase targetCount (slider moves up)
    const newTargetCount = Math.min(10, selectedModels.length + 1);
    setTargetCount(newTargetCount);
    console.log('  → Slider adjusted:', targetCount, '→', newTargetCount);

    // Persist custom models to user profile
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          customModels: newCustomModels
        }, { merge: true });
        console.log('  → Saved to user profile');
      } catch (error) {
        console.error('Error saving custom models to user profile:', error);
      }
    }

    // Close search and clear query
    setIsModelSearchOpen(false);
    setModelSearchQuery('');
  };

  // Handle removing a model - switches to custom mode
  const handleRemoveModel = useCallback(async (modelId: string) => {
    console.log('❌ Removing model:', modelId);
    
    // Prevent removing if only 1 model left
    if (selectedModels.length <= 1) {
      console.warn('Cannot remove model: Must have at least 1 model');
      return;
    }
    
    // Prevent duplicate removal
    if (!selectedModels.includes(modelId)) {
      console.warn('Model not in list');
      return;
    }
    
    // Mark that user has customized (enables custom slider position)
    setHasCustomized(true);
    
    // Create new custom models list without removed model
    const newCustomModels = selectedModels.filter(id => id !== modelId);
    setCustomModels(newCustomModels);
    console.log('  → Updated custom models:', newCustomModels);
    
    // Switch to custom mode
    setPrioritize('custom');
    console.log('  → Switched to custom mode');
    
    // Also remove from inactive list if present
    setInactiveModels(prev => {
      const newSet = new Set(prev);
      newSet.delete(modelId);
      return newSet;
    });
    
    // Decrease targetCount (slider moves down)
    const newTargetCount = Math.max(1, selectedModels.length - 1);
    setTargetCount(newTargetCount);
    console.log('  → Slider adjusted:', targetCount, '→', newTargetCount);
    
    // Persist custom models to user profile
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          customModels: newCustomModels
        }, { merge: true });
        console.log('  → Saved to user profile');
      } catch (error) {
        console.error('Error saving custom models to user profile:', error);
      }
    }
  }, [selectedModels, user]);

  // Handle sidebar resize
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<number | null>(null);
  const sidebarStartWidthRef = useRef<number | null>(null);
  const isResizingRef = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartRef.current = e.clientX;
    sidebarStartWidthRef.current = sidebarWidth;
    isResizingRef.current = true;
    setIsResizing(true);
  }, [sidebarWidth]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current || resizeStartRef.current === null || sidebarStartWidthRef.current === null) {
      return;
    }

    const deltaX = e.clientX - resizeStartRef.current;
    const minWidth = 200; // Minimum sidebar width
    const maxWidth = 600; // Maximum sidebar width
    const newWidth = Math.min(Math.max(sidebarStartWidthRef.current + deltaX, minWidth), maxWidth);
    
    setSidebarWidth(newWidth);
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    resizeStartRef.current = null;
    sidebarStartWidthRef.current = null;
  }, []);

  // Attach mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => handleResizeMove(e);
      const handleMouseUp = () => handleResizeEnd();
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Handle renaming a conversation
  const handleRenameConversation = async (conversationId: string, newTitle: string) => {
    try {
      await ConversationService.updateConversationTitle(conversationId, newTitle);
      console.log('Conversation renamed successfully:', conversationId, newTitle);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      throw error;
    }
  };

  // 🎯 PHASE 1: Determine if user can send next prompt
  // P1 can always be sent. For Pn+1, must wait until first response from Pn arrives
  const canSendNextPrompt = useMemo(() => {
    const userMessages = messages
      .filter(m => m.role === 'user' && !m.overwritten)
      .sort((a, b) => (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0));

    // If no messages yet, can send P1
    if (userMessages.length === 0) {
      return true;
    }

    // Get the last prompt
    const lastPrompt = userMessages[userMessages.length - 1];

    // Can send if last prompt received at least one response
    return !!lastPrompt.firstResponseReceivedAt;
  }, [messages]);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <ChatSidebar 
        isOpen={isSidebarOpen} 
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        conversations={conversations}
        publishedConversations={publishedConversations}
        currentConversationId={currentConversationId || undefined}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onRenameConversation={handleRenameConversation}
        onPublishConversation={publishCurrentConversation}
        onUnpublishConversation={unpublishCurrentConversation}
        onRight2Privacy={right2Privacy}
        highlighting={highlighting}
        tagColors={tagColors}
        modelColorAssignments={modelColorAssignments}
        activeModels={selectedModels.filter(id => !inactiveModels.has(id))}
        validOpenRouterModelIds={validOpenRouterModelIds}
        width={sidebarWidth}
        prioritySetting={prioritize}
        localGradeState={localGradeState}
        messages={messages} // NEW: Pass messages to parse matrix rankings
        pendingConversationId={pendingConversationId}
        currentUserId={user?.uid} // NEW: Pass current user ID for "you" display
      />
      
      {/* Spacer for sidebar - only takes up space when sidebar is open */}
      {isSidebarOpen && (
        <div style={{ width: `${sidebarWidth}px`, minWidth: `${sidebarWidth}px`, flexShrink: 0 }} className="hidden lg:block" />
      )}
      
      {/* Resize Handle - Only visible on desktop (lg breakpoint) when sidebar is open */}
      {isSidebarOpen && (
      <div
        className={`hidden lg:flex items-center justify-center w-1 hover:w-1.5 bg-border hover:bg-primary/50 cursor-col-resize transition-all relative z-10 group ${
          isResizing ? 'bg-primary w-1.5' : ''
        }`}
        onMouseDown={handleResizeStart}
        style={{ minWidth: '4px' }}
      >
        {/* Visual indicator for better UX */}
        <div className={`absolute inset-y-0 w-0.5 bg-border group-hover:bg-primary/70 transition-colors ${
          isResizing ? 'bg-primary' : ''
        }`} />
      </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Desktop-only: Fixed top-left controls - always visible on desktop */}
        {/* When header is collapsed AND status section is visible, move down to avoid overlap with coaching/prompts/dots */}
        {(() => {
          const statusSectionVisible = messages.some(m => m.role === 'user');
          const shouldMoveDown = isTopSectionCollapsed && statusSectionVisible;
          // When moved down: position in the gap between dots row and first message
          // Accounts for: coaching line (~44px) + prompts row (~40px) + dots row (~48px) - positioned just below dots
          const topPosition = shouldMoveDown ? 'top-[100px]' : 'top-4';
          
          return (
            <div className={`hidden lg:flex absolute ${topPosition} left-4 z-50 items-center gap-2`}>
              {/* Sidebar toggle */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
                title={isSidebarOpen ? "Hide sidebar" : "Show sidebar"}
              >
                {isSidebarOpen ? (
                  <ChevronLeft className="h-5 w-5 text-primary" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-primary" />
                )}
              </button>
              
              {/* Header windowshade control */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleWindowshades}
                className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
                title={isTopSectionCollapsed ? "Show controls" : "Hide controls"}
              >
                {isTopSectionCollapsed ? (
                  <ChevronDown className="h-5 w-5 text-primary" />
                ) : (
                  <ChevronUp className="h-5 w-5 text-primary" />
                )}
              </Button>
              
              {/* Theme toggle */}
              <ThemeToggle />
            </div>
          );
        })()}

        {/* Desktop-only: Fixed bottom-left prompt control - only show when there are messages (not in new conversation view) */}
        {messages.length > 0 && (
          <div className="hidden lg:flex absolute bottom-4 left-4 z-50 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleBottomSection}
              className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
              title={isBottomSectionCollapsed ? "Show prompt box" : "Hide prompt box"}
            >
              {isBottomSectionCollapsed ? (
                <ChevronUp className="h-5 w-5 text-primary" />
              ) : (
                <ChevronDown className="h-5 w-5 text-primary" />
              )}
            </Button>
          </div>
        )}

        {/* Header with parameter sliders */}
        <div 
          ref={headerSectionRef}
          className={`flex items-center justify-center p-4 pt-2 lg:pt-16 border-b border-border bg-background relative transition-all duration-300 ${
            isTopSectionCollapsed ? 'hidden' : 'block'
          }`}
        >
          {/* Parameter Sliders and Models - stacked vertically */}
          <div className="flex flex-col items-center gap-4">
            {/* Sliders Row - only visible when peer review settings is expanded */}
            {isPeerReviewSettingsExpanded && (
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 w-full px-4">
                {/* Prioritize Slider */}
                <div className="flex flex-col items-center gap-2">
                  <HoverHelpTooltip
                    helpText={getHelpText('prioritize') || ''}
                    enabled={!shouldShowHelp('prioritize', hasHelp('prioritize')) && hasHelp('prioritize')}
                  >
                    <span className="text-sm font-semibold flex items-center gap-1">
                      prioritize
                      {shouldShowHelp('prioritize', hasHelp('prioritize')) && (
                        <HelpTooltip
                          helpText={getHelpText('prioritize') || ''}
                          onHelpViewed={() => markInteracted('prioritize')}
                          size="sm"
                        />
                      )}
                    </span>
                  </HoverHelpTooltip>
                  <div className={`relative ${hasCustomized ? 'w-36 sm:w-44' : 'w-28 sm:w-36'}`}>
                    <Slider
                      value={[
                        (hasCustomized 
                          ? ['free', 'speed', 'balance', 'value', 'intelligence', 'custom']
                          : ['free', 'speed', 'balance', 'value', 'intelligence']
                        ).indexOf(prioritize)
                      ]}
                      onValueChange={handlePrioritizeChange}
                      min={0}
                      max={hasCustomized ? 5 : 4}
                      step={1}
                      className={hasCustomized ? 'w-36 sm:w-44' : 'w-28 sm:w-36'}
                    />
                  </div>
                  <span 
                    className={`text-xs whitespace-nowrap ${
                      prioritize === 'custom' 
                        ? 'text-foreground font-semibold' 
                        : 'text-muted-foreground'
                    }`}
                  >
                    {({
                      free: 'free',
                      speed: 'speed',
                      balance: 'speed/IQ balance',
                      value: 'IQ/$ balance',
                      intelligence: 'intelligence (IQ)',
                      custom: 'custom',
                    } as Record<string, string>)[prioritize]}
                  </span>
                </div>

                {/* AIs Parameter Slider */}
                <div className="flex flex-col items-center gap-2">
                  <HoverHelpTooltip
                    helpText={getHelpText('ais') || ''}
                    enabled={!shouldShowHelp('ais', hasHelp('ais')) && hasHelp('ais')}
                  >
                    <span className="text-sm font-semibold flex items-center gap-1">
                      AIs
                      {shouldShowHelp('ais', hasHelp('ais')) && (
                        <HelpTooltip
                          helpText={getHelpText('ais') || ''}
                          onHelpViewed={() => markInteracted('ais')}
                          size="sm"
                        />
                      )}
                    </span>
                  </HoverHelpTooltip>
                  <div className="relative w-24 sm:w-32">
                    <Slider
                      value={[targetCount]}
                      onValueChange={handleTargetCountChange}
                      onValueCommit={handleTargetCountCommit}
                      min={1}
                      max={plusHumanEnabled ? 9 : 10}
                      step={1}
                      className="w-24 sm:w-32"
                    />
                  </div>
                  <span className={`text-xs text-muted-foreground ${
                    selectedModels.length - inactiveModels.size !== targetCount && !isAisSliderDragging ? 'italic' : ''
                  }`}>
                    {selectedModels.length - inactiveModels.size}{selectedModels.length - inactiveModels.size !== targetCount && !isAisSliderDragging ? '*' : ''}
                    {modelListLastUpdated && (() => {
                      const updated = new Date(modelListLastUpdated);
                      const now = new Date();
                      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const startOfUpdated = new Date(updated.getFullYear(), updated.getMonth(), updated.getDate());
                      const diffDays = Math.round((startOfToday.getTime() - startOfUpdated.getTime()) / (1000 * 60 * 60 * 24));
                      const label = diffDays === 0 ? 'today' : diffDays === 1 ? 'yesterday' : `${diffDays} days ago`;
                      return <span className="text-muted-foreground/60"> (updated {label})</span>;
                    })()}
                  </span>
                </div>

                {/* Peer Review Level Slider */}
                <div className="flex flex-col items-center gap-2">
                  <HoverHelpTooltip
                    helpText={getHelpText('peer review') || ''}
                    enabled={!shouldShowHelp('peer review', hasHelp('peer review')) && hasHelp('peer review')}
                  >
                    <span className="text-sm font-semibold flex items-center gap-1">
                      peer review
                      {shouldShowHelp('peer review', hasHelp('peer review')) && (
                        <HelpTooltip
                          helpText={getHelpText('peer review') || ''}
                          onHelpViewed={() => markInteracted('peer review')}
                          size="sm"
                        />
                      )}
                    </span>
                  </HoverHelpTooltip>
                  <div className="relative w-24 sm:w-32">
                    <Slider
                      value={[typeof currentPeerReviewLevel === 'number' ? currentPeerReviewLevel - 1 : 0]}
                      onValueChange={handlePeerReviewLevelChange}
                      min={0}
                      max={3}
                      step={1}
                      className="w-24 sm:w-32"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {currentPeerReviewLevel === 'custom' ? 'custom' : `level ${currentPeerReviewLevel}`}
                  </span>
                </div>

                {/* Custom AIs Toggle - opens model search overlay */}
                <HoverHelpTooltip
                  helpText={getHelpText('custom ais') || ''}
                  enabled={!shouldShowHelp('custom ais', hasHelp('custom ais')) && hasHelp('custom ais')}
                >
                <button
                  onClick={() => {
                    if (requiresPaymentGate()) return;
                    const wasOpen = isModelSearchOpen;
                    setIsModelSearchOpen(!isModelSearchOpen);
                    // Fetch models when opening search (but only once per session)
                    if (!wasOpen && allOpenRouterModels.length < 50) {
                      fetchAllOpenRouterModels();
                    }
                  }}
                  disabled={selectedModels.length >= 10}
                  className={`inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
                    transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
                    ${isModelSearchOpen ? 'text-white border border-white/70 dark:border-white/70' : 'text-gray-400 border border-transparent'}
                    ${selectedModels.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={selectedModels.length >= 10 ? 'Cannot add: Maximum of 10 models allowed' : (isModelSearchOpen ? 'Hide model search' : 'Search for custom AI models')}
                >
                  custom AIs
                  {shouldShowHelp('custom ais', hasHelp('custom ais')) && (
                    <HelpTooltip
                      helpText={getHelpText('custom ais') || ''}
                      onHelpViewed={() => markInteracted('custom ais')}
                      size="sm"
                    />
                  )}
                </button>
                </HoverHelpTooltip>

                {/* Custom Review Toggle - opens rubric wizard overlay */}
                <HoverHelpTooltip
                  helpText={getHelpText('custom review') || ''}
                  enabled={!shouldShowHelp('custom review', hasHelp('custom review')) && hasHelp('custom review')}
                >
                <CustomReviewButton
                  rubrics={customRubrics}
                  activeRubricId={activeRubricId}
                  activeRubric={activeRubric}
                  onToggle={setActiveRubricId}
                  onOpenWizard={() => {
                    if (requiresPaymentGate()) return;
                    setIsCustomReviewWizardOpen(true);
                  }}
                  showHelp={shouldShowHelp('custom review', hasHelp('custom review'))}
                  helpText={getHelpText('custom review') || ''}
                  onHelpViewed={() => markInteracted('custom review')}
                />
                </HoverHelpTooltip>
              </div>
            )}

            {/* Selected Models Display - Full Width Below Sliders */}
            <div className="flex flex-wrap items-center justify-center gap-2 w-full px-4 relative">
              {selectedModels.map((modelId, modelIndex) => {
                const isActive = !inactiveModels.has(modelId);
                const isCandidate = candidateModels.includes(modelId);
                const assignedColor = modelColorAssignments.get(modelId);
                const isMarginToggled = marginToggledModels.has(modelId);
                
                // Get model's inherent reasoning requirement
                const modelData = allOpenRouterModels.find(m => m.id === modelId);
                const modelRequiresHighReasoning = modelData?.defaultReasoningEffort === 'high';
                
                // Format display name based on user's reasoning slider:
                // - "high": ALL models show (high)
                // - "default": Only models that REQUIRE high reasoning show (high)
                // - "low": NO models show any suffix (user explicitly overriding to low)
                let displayName = modelId;
                if (reasoningEffort === 'high') {
                  displayName = `${modelId} (high)`;
                } else if (reasoningEffort === 'default' && modelRequiresHighReasoning) {
                  displayName = `${modelId} (high)`;
                }
                
                const isModelInactive = inactiveModels.has(modelId);
                
                return (
                  <div 
                    key={`${modelId}-${modelIndex}`}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
                      isModelInactive
                        ? 'bg-muted/20 text-muted-foreground/50 opacity-50'
                        : isActive 
                          ? 'bg-muted text-foreground' 
                          : 'bg-muted/30 text-muted-foreground'
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleModel(modelId);
                      }}
                      disabled={selectedModels.filter(id => !inactiveModels.has(id)).length <= 1 && !inactiveModels.has(modelId)}
                      className={`rounded p-0.5 transition-colors -ml-1 ${
                        isMarginToggled 
                          ? 'bg-gray-600 text-gray-400' 
                          : 'hover:bg-background/50 text-muted-foreground'
                      } ${selectedModels.filter(id => !inactiveModels.has(id)).length <= 1 && !inactiveModels.has(modelId) ? 'cursor-not-allowed opacity-50' : ''}`}
                      title={(() => {
                        const pricing = modelPricing.get(modelId);
                        if (!pricing?.prompt || !pricing?.completion) {
                          return 'cost data unavailable';
                        }
                        // Calculate blended cost per million tokens (3:1 prompt:completion ratio)
                        const promptPerToken = pricing.prompt;
                        const completionPerToken = pricing.completion;
                        const blendedCost = ((promptPerToken * 3 + completionPerToken * 1) / 4) * 1_000_000;
                        
                        // Calculate percentile rank
                        const percentile = CostTiersService.getCostPercentile(
                          pricing.prompt,
                          pricing.completion,
                          costTiers
                        );
                        
                        if (percentile === null) {
                          return `cost: $${blendedCost.toFixed(4)} per 1M token`;
                        }
                        
                        // Get ordinal suffix
                        const getOrdinalSuffix = (n: number) => {
                          if (n === 0) return 'th';
                          const lastDigit = n % 10;
                          const lastTwoDigits = n % 100;
                          if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return 'th';
                          if (lastDigit === 1) return 'st';
                          if (lastDigit === 2) return 'nd';
                          if (lastDigit === 3) return 'rd';
                          return 'th';
                        };
                        
                        return `cost: ${percentile}${getOrdinalSuffix(percentile)} percentile (lower better) = $${blendedCost.toFixed(4)} per 1M token | click to ${inactiveModels.has(modelId) ? 'activate' : 'deactivate'}`;
                      })()}
                    >
                      {(() => {
                        const pricing = modelPricing.get(modelId);
                        const costTier = CostTiersService.getCostTier(
                          modelId,
                          pricing?.prompt ?? null,
                          pricing?.completion ?? null,
                          costTiers
                        );
                        return (
                          <span className={`text-xs font-medium ${
                            isActive ? 'opacity-100' : 'opacity-40'
                          }`}>
                            {costTier}
                          </span>
                        );
                      })()}
                    </button>
                    {assignedColor ? (
                      (() => {
                        // Extract creator name for help lookup (e.g., "google" from "google/gemini-2.5-flash")
                        const creatorName = modelId.includes('/') ? modelId.split('/')[0] : modelId;
                        const chipHelpText = getHelpText(creatorName);
                        const chipShowHelp = shouldShowHelp(creatorName, hasHelp(creatorName));
                        // Show hover tooltip when questionmark is hidden but help text exists
                        const showHoverTooltip = !chipShowHelp && !!chipHelpText;
                        
                        const chip = (
                          <ModelChip
                            modelId={modelId}
                            displayName={displayName}
                            isActive={highlighting.isModelActive(modelId)}
                            backgroundColor={assignedColor}
                            onClick={() => {
                              highlighting.toggleModelHighlight(modelId, assignedColor);
                              // Mark as interacted when user toggles the chip
                              if (chipHelpText) {
                                markInteracted(creatorName);
                              }
                            }}
                            className="font-mono"
                            isMarginDisabled={isMarginToggled}
                            showModelNames={showModelNames}
                            modelName={modelData?.name || null}
                            onDoubleClick={handleToggleChipView}
                            helpText={chipHelpText}
                            showHelpIcon={chipShowHelp}
                            onHelpViewed={() => markInteracted(creatorName)}
                          />
                        );
                        
                        // Wrap with hover tooltip when questionmark is hidden
                        return showHoverTooltip ? (
                          <HoverHelpTooltip helpText={chipHelpText || ''} enabled={true}>
                            {chip}
                          </HoverHelpTooltip>
                        ) : chip;
                      })()
                    ) : (
                      <span className="font-mono">{displayName}</span>
                    )}
                    {/* Toggle Model Active/Inactive Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleModel(modelId);
                      }}
                      disabled={selectedModels.filter(id => !inactiveModels.has(id)).length <= 1 && !inactiveModels.has(modelId)}
                      className="ml-1 p-0.5 hover:bg-background/50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={inactiveModels.has(modelId) ? 'Reactivate model' : 'Deactivate model'}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  </div>
                );
              })}
              
              {/* "You" Chip - shown when +human is ON and TxT is ON */}
              {plusHumanEnabled && txtEnabled && user?.uid && (
                <ModelChip
                  modelId={user.uid}
                  displayName="you"
                  isActive={false}
                  backgroundColor={modelColorAssignments.get(user.uid) || '#3b82f6'}
                  onClick={() => {}}
                  className="font-mono"
                  isMarginDisabled={false}
                />
              )}
              
              {/* Peer Review Settings Button - toggles visibility of sliders */}
              <button
                onClick={() => setIsPeerReviewSettingsExpanded(!isPeerReviewSettingsExpanded)}
                className={`inline-flex items-center justify-center gap-1 px-2 h-7 rounded text-xs font-medium
                  transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
                  ${isPeerReviewSettingsExpanded ? 'text-white' : 'text-gray-400'}`}
                title={isPeerReviewSettingsExpanded ? 'Hide peer review settings' : 'Show peer review settings'}
              >
                {/* Custom two-line vertical sliders icon */}
                <svg className="h-3 w-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="8" y1="4" x2="8" y2="20" />
                  <circle cx="8" cy="9" r="2" fill="currentColor" />
                  <line x1="16" y1="4" x2="16" y2="20" />
                  <circle cx="16" cy="15" r="2" fill="currentColor" />
                </svg>
                <span className="flex flex-col leading-none text-[10px]">
                  <span>peer review</span>
                  <span>settings</span>
                </span>
              </button>
              
              {/* Model Search Box */}
              {isModelSearchOpen && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 z-50 mt-2 w-full max-w-md bg-background border border-border rounded-lg shadow-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="text"
                      value={modelSearchQuery}
                      onChange={(e) => setModelSearchQuery(e.target.value)}
                      placeholder={`search ${availableModelsCount} models`}
                      className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsModelSearchOpen(false);
                        setModelSearchQuery('');
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {isLoadingModels ? (
                      <div className="text-sm text-muted-foreground text-center py-4">Loading models...</div>
                    ) : filteredModels.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center py-4">No models found</div>
                    ) : (
                      <div className="space-y-1">
                        {filteredModels.map((model) => {
                          const isAlreadySelected = selectedModels.includes(model.id);
                          const isMaxReached = selectedModels.length >= 10;
                          return (
                            <button
                              key={model.id}
                              onClick={() => handleAddModel(model.id)}
                              disabled={isAlreadySelected || isMaxReached}
                              title={
                                isAlreadySelected 
                                  ? 'Model already in header' 
                                  : isMaxReached 
                                    ? 'Cannot add: Maximum of 10 models allowed'
                                    : 'Add model'
                              }
                              className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${
                                isAlreadySelected || isMaxReached
                                  ? 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                                  : 'hover:bg-muted text-foreground cursor-pointer'
                              }`}
                            >
                            <div className="font-mono">{model.id}</div>
                            {model.name && (
                              <div className="text-muted-foreground mt-0.5">{model.name}</div>
                            )}
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile-only: Floating controls at top-left - ONLY show in new conversation state (no status bar visible) */}
        {/* Also check !urlConversationId to avoid race condition when loading existing conversation */}
        {!urlConversationId && !messages.some(m => m.role === 'user') && (
          <div 
            className="lg:hidden absolute left-4 z-50 flex items-center gap-2"
            style={{ top: isTopSectionCollapsed ? '16px' : `${headerHeight + 12}px` }}
          >
            {/* Sidebar toggle */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
              title={isSidebarOpen ? "Hide sidebar" : "Show sidebar"}
            >
              {isSidebarOpen ? (
                <ChevronLeft className="h-5 w-5 text-primary" />
              ) : (
                <ChevronRight className="h-5 w-5 text-primary" />
              )}
            </button>
            
            {/* Windowshade control */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleWindowshades}
              className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
              title={isTopSectionCollapsed ? "Show controls" : "Hide controls"}
            >
              {isTopSectionCollapsed ? (
                <ChevronDown className="h-5 w-5 text-primary" />
              ) : (
                <ChevronUp className="h-5 w-5 text-primary" />
              )}
            </Button>
            
            {/* Theme toggle */}
            <ThemeToggle />
          </div>
        )}

        {/* Chat window */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Status Bar - Fixed at top of chat area (show only when current conversation has user messages) */}
          {messages.some(m => m.role === 'user') && (
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm">
              {/* Coaching Line - above prompts line (only shown when coaching is enabled) */}
              {messages.some(m => m.role === 'user' && !m.overwritten) && isCoachingEnabled && (
                <div className="px-4 py-2 border-b border-border/30">
                  <CoachingLine
                    pointer={coachingPointer}
                    fullPrompt={coachingFullPrompt}
                    apiMetadata={coachingApiMetadata}
                    isLoading={isCoachingLoading}
                    isEnabled={isCoachingEnabled}
                    onToggle={toggleCoaching}
                    onPrevious={goToPreviousCoaching}
                    onNext={goToNextCoaching}
                    hasPrevious={coachingHasPrevious}
                    hasNext={coachingHasNext}
                    showHelp={shouldShowHelp('_coaching row', hasHelp('_coaching row'))}
                    helpText={getHelpText('_coaching row') || ''}
                    onHelpViewed={() => markInteracted('_coaching row')}
                    prodMode={prodMode}
                  />
                </div>
              )}
              
              {/* 🎯 PHASE 1: Prompt Status Buttons Row - P1, P2, etc line + honor system button */}
              {messages.some(m => m.role === 'user' && !m.overwritten) && (
                <HoverHelpTooltip
                  helpText={getHelpText('_prompts_box row') || ''}
                  enabled={!shouldShowHelp('_prompts_box row', hasHelp('_prompts_box row')) && hasHelp('_prompts_box row')}
                >
                <div className="px-4 py-2 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const sortedPrompts = messages
                        .filter(m => m.role === 'user' && !m.overwritten)
                        .sort((a, b) => (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0));
                      
                      return sortedPrompts.map((prompt, index) => {
                        const isPromptProcessing = !prompt.eop && !prompt.stoppedBeforeEOP;
                        const isFocused = visiblePromptIndex === index;
                        return (
                          <PromptStatusButtons
                            key={prompt.id}
                            prompt={prompt}
                            promptNumber={index + 1}
                            isProcessing={isPromptProcessing}
                            isFocused={isFocused}
                            onStop={handlePromptStop}
                            onScrollToPrompt={handleScrollToPrompt}
                            onInteract={() => markInteracted('_prompts_box row')}
                          />
                        );
                      });
                    })()}
                    
                    {/* Account Button - shows user email/balance for EUMPWU users, or legacy honor system */}
                    <button
                      onClick={() => {
                        if (user?.email && personalBalance?.hasKey) {
                          // EUMPWU user - toggle account details
                          setIsHonorSystemExpanded(!isHonorSystemExpanded);
                        } else {
                          // Anonymous user - open Stripe payment
                          handleContributeClick();
                        }
                      }}
                      className={`flex items-center justify-center gap-1 h-6 px-2 rounded transition-all cursor-pointer ${
                        isHonorSystemExpanded 
                          ? 'bg-foreground/20 hover:bg-foreground/25' 
                          : 'bg-foreground/10 hover:bg-foreground/15'
                      }`}
                      title={
                        user?.email && personalBalance?.hasKey
                          ? (isHonorSystemExpanded ? 'Hide account details' : 'Show account details')
                          : user?.email
                            ? 'Your sub-key remaining | OpenRouter account credits (updated on each send system-wide) & change vs previous send'
                            : 'Sign up and add funds'}
                    >
                      <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                        {user?.email &&
                        personalBalance &&
                        (personalBalance.hasKey ||
                          personalBalance.openRouterManagementRemaining != null) ? (
                          <>
                            {user.email.split('@')[0]}:
                            {personalBalance.hasKey ? (
                              <> ${personalBalance.balance.toFixed(2)}</>
                            ) : (
                              <> —</>
                            )}
                            {personalBalance.openRouterManagementRemaining != null ? (
                              <>
                                {' '}
                                | ${personalBalance.openRouterManagementRemaining.toFixed(2)}
                                {personalBalance.openRouterManagementDelta != null ? (
                                  <>
                                    {' '}
                                    (
                                    {personalBalance.openRouterManagementDelta >= 0 ? '+' : ''}
                                    ${personalBalance.openRouterManagementDelta.toFixed(2)})
                                  </>
                                ) : null}
                              </>
                            ) : null}
                          </>
                        ) : (
                          // Anonymous user - prompt to sign up
                          <>anonymous</>
                        )}
                      </span>
                    </button>

                    {/* Help questionmark for prompts box row */}
                    {shouldShowHelp('_prompts_box row', hasHelp('_prompts_box row')) && (
                      <HelpTooltip
                        helpText={getHelpText('_prompts_box row') || ''}
                        onHelpViewed={() => markInteracted('_prompts_box row')}
                        size="sm"
                      />
                    )}

                    {/* icoach button - shown when coaching is disabled, allows re-enabling */}
                    {!isCoachingEnabled && (
                      <button
                        onClick={toggleCoaching}
                        className="flex items-center justify-center gap-1 h-6 px-2 rounded transition-all cursor-pointer bg-foreground/10 hover:bg-foreground/15"
                        title="Enable coaching"
                      >
                        <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                          icoach
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                </HoverHelpTooltip>
              )}
              
              {/* Response Dots Row - Dot line with mobile triplet controls */}
              <div className="px-4 py-3 flex items-center justify-center relative">
                {/* Mobile-only: Triplet controls - left side, absolutely positioned to not affect centered dots */}
                <div className="lg:hidden absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
                  {areMobileControlsExpanded ? (
                    // Expanded state: show all 3 controls with collapse button
                    <>
                      {/* Sidebar toggle */}
                      <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
                        title={isSidebarOpen ? "Hide sidebar" : "Show sidebar"}
                      >
                        {isSidebarOpen ? (
                          <ChevronLeft className="h-5 w-5 text-primary" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-primary" />
                        )}
                      </button>
                      
                      {/* Windowshade control */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleWindowshades}
                        className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
                        title={isTopSectionCollapsed ? "Show controls" : "Hide controls"}
                      >
                        {isTopSectionCollapsed ? (
                          <ChevronDown className="h-5 w-5 text-primary" />
                        ) : (
                          <ChevronUp className="h-5 w-5 text-primary" />
                        )}
                      </Button>
                      
                      {/* Theme toggle */}
                      <ThemeToggle />
                      
                      {/* Collapse button */}
                      <button
                        onClick={() => setAreMobileControlsExpanded(false)}
                        className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
                        title="Collapse controls"
                      >
                        <X className="h-4 w-4 text-primary" />
                      </button>
                    </>
                  ) : (
                    // Collapsed state: show only controls that fit + expander if needed
                    <>
                      {/* Control 1: Sidebar toggle - always show if visibleCount >= 1 */}
                      {visibleMobileControlsCount >= 1 && (
                        <button
                          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                          className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
                          title={isSidebarOpen ? "Hide sidebar" : "Show sidebar"}
                        >
                          {isSidebarOpen ? (
                            <ChevronLeft className="h-5 w-5 text-primary" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-primary" />
                          )}
                        </button>
                      )}
                      
                      {/* Control 2: Windowshade - show if visibleCount >= 2 */}
                      {visibleMobileControlsCount >= 2 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={toggleWindowshades}
                          className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
                          title={isTopSectionCollapsed ? "Show controls" : "Hide controls"}
                        >
                          {isTopSectionCollapsed ? (
                            <ChevronDown className="h-5 w-5 text-primary" />
                          ) : (
                            <ChevronUp className="h-5 w-5 text-primary" />
                          )}
                        </Button>
                      )}
                      
                      {/* Control 3: Theme toggle - show if visibleCount >= 3 */}
                      {visibleMobileControlsCount >= 3 && (
                        <ThemeToggle />
                      )}
                      
                      {/* Expander button - show if not all 3 controls are visible */}
                      {visibleMobileControlsCount < 3 && (
                        <button
                          onClick={() => setAreMobileControlsExpanded(true)}
                          className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full flex items-center justify-center"
                          title="Show all controls"
                        >
                          <MoreHorizontal className="h-5 w-5 text-primary" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                
                {/* Response Dots - Center */}
                <HoverHelpTooltip
                  helpText={getHelpText('_dots row') || ''}
                  enabled={!shouldShowHelp('_dots row', hasHelp('_dots row')) && hasHelp('_dots row')}
                >
                <div className="flex items-center justify-center gap-2">
                  <ResponseDots
                    messages={messages}
                    selectedModelsCount={selectedModels.length}
                    modelColorAssignments={dotColorAssignments}
                    onDotClick={handleDotClick}
                    plusHumanEnabled={plusHumanEnabled}
                    txtEnabled={txtEnabled}
                    userId={user?.uid}
                    onIonoffArrival={setIonoffArrived}
                    onVisiblePromptChange={setVisiblePromptIndex}
                    scrollContainerRef={scrollContainerRef}
                    onInteract={() => markInteracted('_dots row')}
                  />
                  {/* Help questionmark for dots row */}
                  {shouldShowHelp('_dots row', hasHelp('_dots row')) && (
                    <HelpTooltip
                      helpText={getHelpText('_dots row') || ''}
                      onHelpViewed={() => markInteracted('_dots row')}
                      size="sm"
                    />
                  )}
                </div>
                </HoverHelpTooltip>
                
                {/* Clean View Toggle - Right side (absolute positioned) - hidden in prodMode */}
                {!prodMode && (
                  <label className="absolute right-4 flex items-center gap-2 cursor-pointer">
                    <button
                      onClick={() => setCleanViewEnabled(!cleanViewEnabled)}
                      className={`
                        relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                        ${cleanViewEnabled ? 'bg-gray-400' : 'bg-gray-600'}
                      `}
                      role="switch"
                      aria-checked={cleanViewEnabled}
                      title={cleanViewEnabled ? 'Clean view enabled - showing only essential messages' : 'Clean view disabled - showing all messages'}
                    >
                      <span
                        className={`
                          inline-block h-3 w-3 transform rounded-full bg-background transition-transform
                          ${cleanViewEnabled ? 'translate-x-5' : 'translate-x-1'}
                        `}
                      />
                    </button>
                    <span className="text-sm text-gray-400">cleanview</span>
                  </label>
                )}
              </div>
            </div>
          )}

            <ChatWindow 
            messages={messages} 
            onSendMessage={handleSendMessage}
            onEditPrompt={handleEditPrompt}
            disabled={authLoading || conversationLoading || !user}
            highlighting={highlighting}
            modelColorAssignments={modelColorAssignments}
            conversationId={currentConversationId}
            webEnabled={webEnabled}
            setWebEnabled={setWebEnabled}
            enterSendsEnabled={enterSendsEnabled}
            setEnterSendsEnabled={setEnterSendsEnabled}
            selfEvalEnabled={selfEvalEnabled}
            setSelfEvalEnabled={setSelfEvalEnabled}
            txtEnabled={txtEnabled}
            setTxtEnabled={setTxtEnabled}
            plusHumanEnabled={plusHumanEnabled}
            setPlusHumanEnabled={setPlusHumanEnabled}
            humanLeaderboardEnabled={humanLeaderboardEnabled}
            setHumanLeaderboardEnabled={setHumanLeaderboardEnabled}
            toolsExpanded={toolsExpanded}
            setToolsExpanded={handleToolsExpandedChange}
            prioritySetting={prioritize}
            targetCount={targetCount}
            cleanViewEnabled={cleanViewEnabled}
            highlightedMessageId={highlightedMessageId}
            showModelNames={showModelNames}
            onToggleModelNames={handleToggleChipView}
            assignStarGrade={assignStarGrade}
            deleteStarGrade={deleteStarGrade}
            resumeFromPause={resumeFromPause}
            canResume={canResume}
            isResuming={isResuming}
            ionoffArrived={ionoffArrived}
            txtPaused={txtPaused}
            txtPausePhase={txtPausePhase}
            scrollContainerRefCallback={(ref) => { scrollContainerRef.current = ref; }}
            txtModelGradesReceived={txtModelGradesReceived}
            txtIonoffGradeReceived={txtIonoffGradeReceived}
            txtResumeMessageId={txtResumeMessageId}
            showAdminInput={showAdminInput}
            setShowAdminInput={setShowAdminInput}
            adminConversationCount={adminConversationCount}
            setAdminConversationCount={setAdminConversationCount}
            onLoadAdminConversations={loadAdminConversations}
            isLoadingAdminConversations={isLoadingAdminConversations}
            prodMode={prodMode}
            localGradeState={localGradeState}
            tagColors={tagColors}
            dismissedIcebreakers={dismissedIcebreakers}
            onDismissIcebreaker={handleDismissIcebreaker}
            isBottomSectionCollapsed={isBottomSectionCollapsed}
            reasoningEffort={reasoningEffort}
            setReasoningEffort={setReasoningEffort}
            activeRubric={activeRubric}
          />
          
          {/* Grade Matrix Generator - generates matrix messages from local grade state */}
          {localGradeState && gradeProcessor && (
            <GradeMatrixGenerator
              localGradeState={localGradeState}
              messages={messages}
              gradeProcessor={gradeProcessor}
              onAddMessage={addLocalMessage}
              txtEnabled={txtEnabled}
            />
          )}
        </div>

        {/* Mobile-only: Bottom windowshade control - shown when prompt box is collapsed and there are messages */}
        {messages.length > 0 && isBottomSectionCollapsed && (
          <div className="lg:hidden absolute bottom-4 left-4 z-50 flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleBottomSection}
              className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
              title="Show prompt box"
            >
              <ChevronUp className="h-5 w-5 text-primary" />
            </Button>
          </div>
        )}

        {/* Chat input section */}
        <div className={`transition-all duration-300 relative border-t border-border ${
          isBottomSectionCollapsed ? 'hidden' : 'block'
        }`}>
          {/* Mobile-only: Bottom windowshade control - shown when prompt box is open and there are messages */}
          {/* Positioned to be equidistant between prompt box top line and section delimiter */}
          {messages.length > 0 && !isBottomSectionCollapsed && (
            <div className="lg:hidden absolute -top-5 left-4 z-10 flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleBottomSection}
                className="h-9 w-9 p-0 hover:bg-muted bg-background border-2 border-primary/30 shadow-md hover:border-primary/50 transition-all rounded-full"
                title="Hide prompt box"
              >
                <ChevronDown className="h-5 w-5 text-primary" />
              </Button>
            </div>
          )}
          
          {messages.length > 0 && (
            <ChatInput 
              onSendMessage={handleSendMessage} 
              onStop={handleStopProcessing}
              isProcessing={isProcessing}
              disabled={authLoading || conversationLoading || !user}
              canSendNextPrompt={canSendNextPrompt}
              isInitialState={false}
              placeholder={instructions.inConversation}
              initialMessage={editingPromptContent}
              editingPromptId={editingPromptId}
              onClearEdit={handleClearEdit}
              webEnabled={webEnabled}
              setWebEnabled={setWebEnabled}
              enterSendsEnabled={enterSendsEnabled}
              setEnterSendsEnabled={setEnterSendsEnabled}
              selfEvalEnabled={selfEvalEnabled}
              setSelfEvalEnabled={setSelfEvalEnabled}
              txtEnabled={txtEnabled}
              setTxtEnabled={setTxtEnabled}
              plusHumanEnabled={plusHumanEnabled}
              setPlusHumanEnabled={setPlusHumanEnabled}
              humanLeaderboardEnabled={humanLeaderboardEnabled}
              setHumanLeaderboardEnabled={setHumanLeaderboardEnabled}
              toolsExpanded={toolsExpanded}
              setToolsExpanded={handleToolsExpandedChange}
              targetCount={targetCount}
              autoHumanDatasetEnabled={autoHumanDatasetEnabled}
              showAdminInput={showAdminInput}
              setShowAdminInput={setShowAdminInput}
              adminConversationCount={adminConversationCount}
              setAdminConversationCount={setAdminConversationCount}
              onLoadAdminConversations={loadAdminConversations}
              isLoadingAdminConversations={isLoadingAdminConversations}
              prodMode={prodMode}
              onNewConversation={handleNewConversation}
              reasoningEffort={reasoningEffort}
              setReasoningEffort={setReasoningEffort}
              getHelpText={getHelpText}
              hasHelp={hasHelp}
              shouldShowHelp={shouldShowHelp}
              markInteracted={markInteracted}
            />
          )}
        </div>
      </div>

      {/* Contribution Notification Toast */}
      {contributionNotification && (
        <div 
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg transition-all ${
            contributionNotification.type === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-amber-600 text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            {contributionNotification.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{contributionNotification.message}</span>
            <button 
              onClick={() => setContributionNotification(null)}
              className="ml-2 hover:opacity-70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Contribute Modal */}
      <ContributeModal
        isOpen={isContributeModalOpen}
        onClose={() => setIsContributeModalOpen(false)}
        isRegisteredUser={!!(personalBalance?.hasKey || isAuthenticatedWithEmail())}
      />

      {/* Coopital Voting Modal */}
      {coopitalVotingBallot && (
        <CoopitalVotingModal
          ballotType={coopitalVotingBallot}
          onClose={() => setCoopitalVotingBallot(null)}
        />
      )}

      {/* Legacy PasswordGateModal and LoginModal removed - EUMPWU enforces payment */}

      {/* Insufficient Funds Modal (EUMPWU) */}
      {isInsufficientFundsModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        >
          <div 
            className={`
              relative w-full max-w-md rounded-2xl shadow-2xl p-6
              ${isDarkMode 
                ? 'bg-gray-900 text-gray-100 border border-gray-700' 
                : 'bg-white text-gray-900 border border-gray-200'
              }
            `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => {
                setIsInsufficientFundsModalOpen(false);
                setPendingMessageData(null);
              }}
              className={`
                absolute top-4 right-4 p-1 rounded-full transition-colors
                ${isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }
              `}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="mb-6">
              <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                💰 Add Funds to Continue
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {userBalanceInfo?.hasKey 
                  ? `Your balance is $${(userBalanceInfo?.balance || 0).toFixed(2)}. Please add funds to send more prompts.`
                  : 'Please add funds to start using ionoff.'
                }
              </p>
            </div>

            {/* Balance info */}
            {userBalanceInfo?.hasKey && (
              <div 
                className={`
                  mb-6 p-4 rounded-lg text-sm
                  ${isDarkMode 
                    ? 'bg-gray-800 border border-gray-700' 
                    : 'bg-gray-50 border border-gray-200'
                  }
                `}
              >
                <div className="flex justify-between mb-2">
                  <span>Current Balance:</span>
                  <span className="font-medium">${(userBalanceInfo?.balance || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Total Limit:</span>
                  <span>${(userBalanceInfo?.limit || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Usage:</span>
                  <span>${(userBalanceInfo?.usage || 0).toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Message */}
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              ionoff uses a pay-for-what-you-use model. Add funds to your account to continue sending prompts to AI models.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsInsufficientFundsModalOpen(false);
                  setPendingMessageData(null);
                }}
                className={`
                  flex-1 py-3 px-4 rounded-lg font-medium transition-colors
                  ${isDarkMode
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                  }
                `}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Store the pending prompt before going to Stripe
                  if (pendingMessageData) {
                    storePendingPrompt(pendingMessageData.content, pendingMessageData.plusHumanContent);
                  }
                  setIsInsufficientFundsModalOpen(false);
                  invalidateBalanceCache(); // Clear cache so next check is fresh
                  setIsContributeModalOpen(true); // Open contribute modal
                }}
                className={`
                  flex-1 py-3 px-4 rounded-lg font-medium transition-all
                  ${isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }
                `}
              >
                Add Funds
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Account / Honor System Modal Overlay */}
      {isHonorSystemExpanded && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-[15vh]"
          onClick={() => setIsHonorSystemExpanded(false)}
        >
          <div 
            className="relative bg-background border border-border rounded-lg p-6 max-w-lg mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsHonorSystemExpanded(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* EUMPWU User View - has personal OpenRouter key */}
            {user?.email && personalBalance?.hasKey ? (
              <div className="space-y-4">
                {/* User Email Header with Actions */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{user.email}</span>
                  <button
                    onClick={() => {
                      setIsHonorSystemExpanded(false);
                      handleContributeClick();
                    }}
                    className="text-xs px-2 py-1 rounded bg-green-600 hover:bg-green-500 text-white transition-colors"
                  >
                    add $
                  </button>
                  <button
                    onClick={async () => {
                      setIsHonorSystemExpanded(false);
                      const { signOut } = await import('../../services/authService');
                      await signOut();
                    }}
                    className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                  >
                    logout
                  </button>
                </div>
                
                {/* Itemized Breakdown */}
                <div className="text-sm space-y-2 font-mono">
                  <div className="flex justify-between">
                    <span>You added:</span>
                    <span className="text-green-500">+${personalBalance.totalPaid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Payment costs<sup>1</sup>:</span>
                    <span className="text-red-400">-${personalBalance.totalStripeFees.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>AI access costs<sup>2</sup>:</span>
                    <span className="text-red-400">-${personalBalance.totalOpenRouterTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>AI usage costs<sup>3</sup>:</span>
                    <span className="text-red-400">-${personalBalance.totalOpenRouterCosts.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Your share of system costs<sup>4</sup>:</span>
                    <span className="text-red-400">-${personalBalance.userShareOfSystemCosts.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground items-center">
                    <span>&ldquo;Try before pay&rdquo; costs<sup>5</sup>:</span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-red-400">-${personalBalance.userShareOfTryBeforePayCosts.toFixed(2)}</span>
                      <button
                        onClick={() => setCoopitalVotingBallot('tryBeforePay')}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 hover:bg-red-500 text-white transition-colors font-sans"
                      >
                        {personalBalance.tryBeforePayThreshold}
                      </button>
                      <span className="text-[10px] text-muted-foreground/50 font-sans">vote</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground items-center">
                    <span>Coopital<sup>6</sup>:</span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-green-500">+${personalBalance.coopitalValue.toFixed(2)}</span>
                      <button
                        onClick={() => setCoopitalVotingBallot('marginRate')}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 hover:bg-green-500 text-white transition-colors font-sans"
                      >
                        {personalBalance.coopitalMarginRate}%
                      </button>
                      <span className="text-[10px] text-muted-foreground/50 font-sans">vote</span>
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-border font-semibold">
                    <span className="font-sans">balance:</span>
                    <span>${(personalBalance.balance - personalBalance.userShareOfSystemCosts - personalBalance.userShareOfTryBeforePayCosts + personalBalance.coopitalValue).toFixed(2)}</span>
                  </div>
                </div>
                
                {/* Fee Notes */}
                <div className="text-xs text-muted-foreground/70 space-y-0.5">
                  <p><sup>1</sup>Stripe charges 2.9% + $0.30 per transaction</p>
                  <p><sup>2</sup>OpenRouter charges 3% Maryland sales tax</p>
                  <p><sup>3</sup>OpenRouter charges 1.055× the per-token fees set by the AI manufacturers</p>
                  <p><sup>4</sup>your share of ionoff system overhead since your first prompt ({personalBalance.coopitalShare > 0 ? `${(personalBalance.coopitalShare * 100).toFixed(2)}%` : '0%'} coopital)</p>
                  <p><sup>5</sup>your share of anonymous user costs since your first prompt (vote to set threshold)</p>
                  <p><sup>6</sup>your share of the cooperative dividend (vote to set margin rate)</p>
                </div>
                
                {/* Coopitalism Explainer */}
                <div className="text-sm text-muted-foreground pt-2 border-t border-border space-y-2">
                  <p>
                    ionoff is a family hobby project, but AIs cost real money to use. 
                    Each user pays for what their individual usage costs. 
                    As a coop member, you share in system overhead and anonymous user costs proportional to your usage ({personalBalance.coopitalShare > 0 ? `${(personalBalance.coopitalShare * 100).toFixed(2)}%` : '0%'} of ${personalBalance.systemTotalAiUsage > 0 ? `$${personalBalance.systemTotalAiUsage.toFixed(2)}` : '$0.00'} total).
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    Vote on the red and green badges above to set cooperative parameters.
                  </p>
                </div>
              </div>
            ) : balanceData ? (
              // Legacy Honor System View
              <div className="space-y-4">
                {/* Community Balance */}
                <div className="flex items-center gap-2">
                  <span className="font-medium">community balance:</span>
                  <BalanceSparkline data={balanceData.sparklineData} />
                  <span>{formatCurrency(balanceData.displayBalance)}</span>
                </div>
                
                {/* User Impact */}
                <div>
                  <span className="font-medium">your impact: </span>
                  <span className={balanceData.userImpact >= 0 ? 'text-green-500' : 'text-red-500'}>
                    {formatCurrency(balanceData.userImpact, true)}
                  </span>
                </div>
                
                {/* Honor System Message */}
                <div className="text-muted-foreground">
                  <span>honor system: please </span>
                  <button
                    onClick={() => {
                      setIsHonorSystemExpanded(false);
                      handleContributeClick();
                    }}
                    className="underline hover:text-foreground transition-colors cursor-pointer"
                  >
                    contribute
                  </button>
                  <span> to make your impact positive. proceeds go only to covering cost of use by all ({balanceData.activeUserCount}).</span>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">Loading balance data...</div>
            )}
          </div>
        </div>
      )}

      {/* Custom Review Rubric Wizard Overlay */}
      <CustomReviewOverlay
        isOpen={isCustomReviewWizardOpen}
        onClose={() => setIsCustomReviewWizardOpen(false)}
        onSave={async (rubricData) => {
          console.log('🔵 ChatLayout onSave called with:', rubricData);
          try {
            const rubricId = await createRubric(rubricData);
            console.log('✅ Rubric created with ID:', rubricId);
            setActiveRubricId(rubricId);
            console.log('✅ Active rubric ID set');
          } catch (error) {
            console.error('❌ ChatLayout onSave error:', error);
            throw error;
          }
        }}
      />
    </div>
  );
};

